import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Search, MapPin, Phone, MessageCircle, Globe,
  Building2, Star, Loader, Copy, CheckCheck, Filter, X,
  GripVertical, ChevronDown, Plus, Target, CheckCircle2, XCircle,
  TrendingUp, Send, ArrowLeft, Sparkles, AlertTriangle, TrendingDown,
  Upload, Users, Trash2, Play, FileSpreadsheet, Bot,
  ShoppingBag, Mail,
} from "lucide-react";
import EmailMassaTab from "@/components/EmailMassaTab";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { AutocompleteInput } from "@/components/AutocompleteInput";

const PLANO_SUGESTOES = [
  "400MEGA", "600MEGA", "800MEGA", "1 GIGA", "5 GIGA", "10 GIGA",
  "Convergência (internet + celular)", "Outros",
];
import { api } from "@/lib/api";

// ── Helpers ───────────────────────────────────────────────────
function fmtBRL(n) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n || 0);
}

const SEGMENTOS = [
  { value: "empresas",         label: "🏬 Qualquer negócio (todos os segmentos)" },
  { value: "restaurante",      label: "🍔 Restaurante / Alimentação" },
  { value: "salão de beleza",  label: "💅 Salão / Estética" },
  { value: "oficina mecânica", label: "🔧 Mecânica / Automóveis" },
  { value: "farmácia",         label: "💊 Farmácia / Saúde" },
  { value: "academia",         label: "💪 Academia / Fitness" },
  { value: "pet shop",         label: "🐾 Pet Shop / Veterinária" },
  { value: "imobiliária",      label: "🏢 Imóveis / Apartamentos" },
  { value: "advocacia",        label: "⚖️ Advocacia / Jurídico" },
  { value: "escola",           label: "📚 Educação / Cursos" },
  { value: "tecnologia",       label: "💻 Tecnologia / Informática" },
  { value: "loja de roupas",   label: "👗 Roupas / Moda" },
  { value: "supermercado",     label: "🛒 Supermercado" },
  { value: "clínica médica",   label: "🏥 Clínica Médica" },
  { value: "hotel",            label: "🏨 Hotel / Hospedagem" },
  { value: "construção",       label: "🏗️ Construção / Reforma" },
];

// ─── Kanban ───────────────────────────────────────────────────
const KANBAN_COLS = [
  { id: "lead",      label: "Lead",          color: "#3b82f6", icon: "🎯" },
  { id: "contato",   label: "Contato Feito", color: "#f59e0b", icon: "📞" },
  { id: "proposta",  label: "Proposta",      color: "#8b5cf6", icon: "📋" },
  { id: "fechado",   label: "Fechado",       color: "#22c55e", icon: "✅" },
  { id: "perdido",   label: "Perdido",       color: "#ef4444", icon: "❌" },
];
const PIPELINE_KEY = "prospect_pipeline_leads"; // legado — mantido só pra eventual migração de dados antigos
const AGENT_DEFAULT_MESSAGE =
  "Oi! Vi que sua empresa pode estar pagando caro ou tendo problema de suporte no plano atual. " +
  "Quer que eu te mande as opções disponíveis pro seu CEP?";

// ─── CopyButton ───────────────────────────────────────────────
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      className="p-1 hover:bg-zinc-800 rounded transition-colors">
      {copied ? <CheckCheck className="h-3 w-3 text-green-400" /> : <Copy className="h-3 w-3 text-zinc-400" />}
    </button>
  );
}

// ─── Chat WhatsApp ────────────────────────────────────────────
function WhatsAppChat({ place, onClose }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const bottomRef = useRef(null);
  const phone = (place.phone || "").replace(/\D/g, "");

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // Carrega o histórico salvo dessa conversa ao abrir o chat
  useEffect(() => {
    if (!phone) { setLoadingHistory(false); return; }
    let active = true;
    setLoadingHistory(true);
    api.get(`/prospect/conversations/direct-message/${phone}`)
      .then(({ data }) => {
        if (!active) return;
        const hist = (data.messages || []).map(m => ({
          id: m.id,
          from: m.direction === "in" ? "them" : "me",
          text: m.text,
          time: new Date(m.created_at),
          status: m.status,
        }));
        setMessages(hist);
      })
      .catch(() => {})
      .finally(() => { if (active) setLoadingHistory(false); });
    return () => { active = false; };
  }, [phone]);

  const pollTimers = useRef([]);

  // Limpa os timers de polling quando o chat é desmontado/fechado
  useEffect(() => {
    return () => { pollTimers.current.forEach(clearInterval); pollTimers.current = []; };
  }, []);

  const pollMessageStatus = (msgId) => {
    let attempts = 0;
    const maxAttempts = 14; // ~21s, tempo de sobra pro envio na Evolution
    const timer = setInterval(async () => {
      attempts++;
      try {
        const { data } = await api.get(`/prospect/conversations/direct-message/${phone}`);
        const found = (data.messages || []).find(m => m.id === msgId);
        if (found && found.status !== "sending") {
          setMessages(prev => prev.map(m => m.id === msgId ? { ...m, status: found.status } : m));
          if (found.status === "no_whatsapp") {
            toast.error("Esse número não tem WhatsApp — a mensagem não foi entregue.");
          } else if (found.status === "failed") {
            toast.error("Falha ao enviar mensagem via WhatsApp.");
          }
          clearInterval(timer);
          pollTimers.current = pollTimers.current.filter(t => t !== timer);
        }
      } catch (e) {
        // erro transitório no polling — tenta de novo no próximo ciclo
      }
      if (attempts >= maxAttempts) {
        clearInterval(timer);
        pollTimers.current = pollTimers.current.filter(t => t !== timer);
      }
    }, 1500);
    pollTimers.current.push(timer);
  };

  const sendMessage = async () => {
    if (!text.trim() || !phone) return;
    const msg = text.trim();
    const tempId = `temp-${Date.now()}`;
    setText("");
    setMessages(prev => [...prev, { id: tempId, from: "me", text: msg, time: new Date(), status: "sending" }]);
    setSending(true);
    try {
      const { data } = await api.post("/prospect/conversations/direct-message", {
        phone,
        text: msg,
        name: place.name,
      });
      const realId = data?.message?.id || tempId;
      setMessages(prev => prev.map(m => m.id === tempId ? { ...m, id: realId, status: "sending" } : m));
      pollMessageStatus(realId);
    } catch (e) {
      setMessages(prev => prev.map(m => m.id === tempId ? { ...m, status: "failed" } : m));
      toast.error("Erro ao enviar mensagem via WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  const digits = phone.startsWith("55") ? phone : `55${phone}`;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 999,
      background: "rgba(0,0,0,0.8)", display: "flex",
      alignItems: "center", justifyContent: "center",
      backdropFilter: "blur(4px)", padding: 16,
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        width: "100%", maxWidth: 480, height: "80vh",
        background: "#131929", borderRadius: 20,
        border: "1px solid #1e2d45",
        boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
        display: "flex", flexDirection: "column", overflow: "hidden",
      }}>
        {/* Header */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #1e2d45", display: "flex", alignItems: "center", gap: 12, background: "#0f1826" }}>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b", padding: 4 }}>
            <ArrowLeft size={18} />
          </button>
          <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#22c55e20", border: "1px solid #22c55e40", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <MessageCircle size={16} color="#22c55e" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#f1f5f9", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{place.name}</div>
            <div style={{ fontSize: 11, color: "#64748b" }}>{phone ? `+${digits}` : "Sem telefone"}</div>
          </div>
          {phone && (
            <a href={`https://wa.me/${digits}`} target="_blank" rel="noreferrer"
              style={{ padding: "6px 10px", background: "#22c55e20", border: "1px solid #22c55e40", borderRadius: 8, color: "#22c55e", fontSize: 11, fontWeight: 600, textDecoration: "none", whiteSpace: "nowrap" }}>
              Abrir WA ↗
            </a>
          )}
        </div>

        {/* Mensagens */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 8 }}>
          {loadingHistory && (
            <div style={{ textAlign: "center", padding: "32px 16px", color: "#475569" }}>
              <Loader size={20} style={{ animation: "spin 1s linear infinite", margin: "0 auto" }} />
            </div>
          )}
          {!loadingHistory && messages.length === 0 && (
            <div style={{ textAlign: "center", padding: "32px 16px", color: "#475569" }}>
              <MessageCircle size={32} style={{ margin: "0 auto 12px", opacity: 0.3 }} />
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Iniciar conversa</div>
              <div style={{ fontSize: 11 }}>A mensagem será enviada pelo seu WhatsApp conectado à SaaS</div>
            </div>
          )}
          {!loadingHistory && messages.map((m, i) => (
            <div key={m.id || i} style={{ display: "flex", justifyContent: m.from === "me" ? "flex-end" : "flex-start" }}>
              <div style={{
                maxWidth: "75%", padding: "8px 12px",
                background: m.from === "me" ? "linear-gradient(135deg, #3b82f6, #2563eb)" : "#1e2d45",
                borderRadius: m.from === "me" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                color: "#f1f5f9", fontSize: 13, lineHeight: 1.5,
              }}>
                <div>{m.text}</div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4, fontSize: 10, color: m.from === "me" ? "rgba(255,255,255,0.6)" : "#64748b", marginTop: 4 }}>
                  <span>{m.time.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</span>
                  {m.from === "me" && (
                    m.status === "sending" ? (
                      <Loader size={11} style={{ animation: "spin 1s linear infinite" }} />
                    ) : m.status === "no_whatsapp" || m.status === "failed" ? (
                      <span title={m.status === "no_whatsapp" ? "Esse número não tem WhatsApp" : "Falha ao enviar"} style={{ display: "flex" }}>
                        <XCircle size={12} color="#ef4444" />
                      </span>
                    ) : (
                      <span title="Enviado" style={{ display: "flex" }}>
                        <CheckCheck size={12} color="#60a5fa" />
                      </span>
                    )
                  )}
                </div>
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div style={{ padding: "12px 16px", borderTop: "1px solid #1e2d45", display: "flex", gap: 10, alignItems: "flex-end", background: "#0f1826" }}>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            placeholder={phone ? "Digite sua mensagem... (Enter para enviar)" : "Este lead não tem telefone"}
            disabled={!phone}
            rows={1}
            style={{
              flex: 1, background: "#1e2d45", border: "1px solid #2e3555",
              borderRadius: 12, color: "#f1f5f9", fontSize: 13,
              padding: "10px 14px", outline: "none", resize: "none",
              fontFamily: "inherit", lineHeight: 1.5,
              opacity: phone ? 1 : 0.4,
            }}
          />
          <button
            onClick={sendMessage}
            disabled={!text.trim() || !phone || sending}
            style={{
              width: 40, height: 40, borderRadius: 12, flexShrink: 0,
              background: text.trim() && phone ? "linear-gradient(135deg, #22c55e, #16a34a)" : "#1e2d45",
              border: "none", cursor: text.trim() && phone ? "pointer" : "not-allowed",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all 0.2s",
            }}
          >
            {sending ? <Loader size={16} color="#fff" style={{ animation: "spin 1s linear infinite" }} /> : <Send size={16} color={text.trim() && phone ? "#fff" : "#475569"} />}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ResultCard ───────────────────────────────────────────────
function ResultCard({ place, onAddToKanban, onOpenChat }) {
  const phone = place.phone || "";
  const digits = phone.replace(/\D/g, "");
  const hasWA = digits.length >= 10;

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 hover:border-zinc-700 transition-colors">
      <div className="flex items-start gap-3 mb-3">
        <div className="h-10 w-10 bg-blue-600/10 rounded-md flex items-center justify-center flex-shrink-0">
          <Building2 className="h-5 w-5 text-blue-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-zinc-100 text-sm leading-tight mb-1">{place.name || "Sem nome"}</div>
          {place.category && (
            <Badge variant="outline" className="text-xs border-blue-500/30 text-blue-400 bg-blue-500/10">{place.category}</Badge>
          )}
          {place.rating > 0 && (
            <div className="flex items-center gap-1 mt-1">
              <Star className="h-3 w-3 text-amber-400 fill-amber-400" />
              <span className="text-xs text-zinc-400">{place.rating.toFixed(1)} ({place.reviews || 0} avaliações)</span>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-1.5 mb-3">
        {place.address && (
          <div className="flex items-start gap-2">
            <MapPin className="h-3.5 w-3.5 text-zinc-500 mt-0.5 flex-shrink-0" />
            <span className="text-xs text-zinc-400 break-words">{place.address}</span>
          </div>
        )}
        {phone ? (
          <div className="flex items-center gap-2">
            <Phone className="h-3.5 w-3.5 text-zinc-500 flex-shrink-0" />
            <span className="text-xs text-zinc-300">{phone}</span>
            <CopyButton text={phone} />
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Phone className="h-3.5 w-3.5 text-zinc-600 flex-shrink-0" />
            <span className="text-xs text-zinc-600 italic">Sem telefone</span>
          </div>
        )}
        {place.website && (
          <div className="flex items-center gap-2">
            <Globe className="h-3.5 w-3.5 text-zinc-500 flex-shrink-0" />
            <a href={place.website} target="_blank" rel="noopener noreferrer"
              className="text-xs text-blue-400 hover:text-blue-300 truncate max-w-xs">
              {place.website.replace(/^https?:\/\//, "").slice(0, 40)}
            </a>
          </div>
        )}
      </div>

      {/* Ações */}
      <div className="flex gap-2 pt-2 border-t border-zinc-800">
        {hasWA && (
          <button onClick={() => onOpenChat(place)}
            className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-green-500/10 border border-green-500/30 text-green-400 text-xs font-semibold hover:bg-green-500/20 transition-colors">
            <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
          </button>
        )}
        <button onClick={() => onAddToKanban(place)}
          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-blue-500/10 border border-blue-500/30 text-blue-400 text-xs font-semibold hover:bg-blue-500/20 transition-colors">
          <Plus className="h-3.5 w-3.5" /> Pipeline
        </button>
      </div>
    </div>
  );
}

// ─── Kanban LeadCard ──────────────────────────────────────────
function LeadDetailsModal({ mode, initialValues, onSubmit, onClose }) {
  const [name, setName] = useState(initialValues?.name || "");
  const [phone, setPhone] = useState(initialValues?.phone || "");
  const [cnpj, setCnpj] = useState(initialValues?.cnpj || "");
  const [plano, setPlano] = useState(initialValues?.plano_negociado || "");
  const [obs, setObs] = useState(initialValues?.observacoes || "");
  const [valor, setValor] = useState(initialValues?.value ? String(initialValues.value) : "");
  const [showSugestoes, setShowSugestoes] = useState(false);
  const [saving, setSaving] = useState(false);

  const sugestoesFiltradas = PLANO_SUGESTOES.filter(p =>
    p.toLowerCase().includes(plano.toLowerCase())
  );

  const handleSubmit = async () => {
    if (!name.trim()) { toast.error("Nome da empresa é obrigatório."); return; }
    if (mode === "add" && !phone.trim()) { toast.error("Informa o telefone do lead."); return; }
    setSaving(true);
    const ok = await onSubmit({
      name: name.trim(),
      phone: phone.trim(),
      cnpj: cnpj.trim(),
      plano_negociado: plano.trim(),
      observacoes: obs.trim(),
      value: valor.trim() ? parseFloat(valor.replace(",", ".")) : 0,
    });
    setSaving(false);
    if (ok) onClose();
  };

  const inputStyle = {
    width: "100%", boxSizing: "border-box", padding: "9px 10px",
    borderRadius: 8, border: "1px solid #2e3555", background: "#1e2d45",
    color: "#f1f5f9", fontSize: 13, marginBottom: 10,
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#131929", borderRadius: 14, padding: 20, width: 340,
          border: "1px solid #2e3555", maxHeight: "85vh", overflowY: "auto",
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4, color: "#f1f5f9" }}>
          {mode === "add" ? "Adicionar lead manualmente" : "Editar detalhes do lead"}
        </div>
        {mode === "add" && (
          <div style={{ fontSize: 11.5, color: "#94a3b8", marginBottom: 14 }}>
            Entra direto na coluna "Leads". Útil pra empresas que você já contactou por fora e quer organizar aqui.
          </div>
        )}

        <label style={{ fontSize: 11, color: "#94a3b8", display: "block", marginBottom: 4 }}>Nome da empresa *</label>
        <input type="text" placeholder="Ex: Restaurante Bom Sabor" value={name}
          onChange={(e) => setName(e.target.value)} style={inputStyle} />

        {mode === "add" && (
          <>
            <label style={{ fontSize: 11, color: "#94a3b8", display: "block", marginBottom: 4 }}>Telefone (com DDD) *</label>
            <input type="text" placeholder="Ex: 11999998888" value={phone}
              onChange={(e) => setPhone(e.target.value)} style={inputStyle} />
          </>
        )}

        <label style={{ fontSize: 11, color: "#94a3b8", display: "block", marginBottom: 4 }}>CNPJ (opcional)</label>
        <input type="text" placeholder="00.000.000/0000-00" value={cnpj}
          onChange={(e) => setCnpj(e.target.value)} style={inputStyle} />

        <label style={{ fontSize: 11, color: "#94a3b8", display: "block", marginBottom: 4 }}>Plano negociado (opcional)</label>
        <div style={{ position: "relative", marginBottom: 10 }}>
          <input
            type="text"
            placeholder="Ex: 600MEGA, ou Outros"
            value={plano}
            onChange={(e) => setPlano(e.target.value)}
            onFocus={() => setShowSugestoes(true)}
            onBlur={() => setTimeout(() => setShowSugestoes(false), 120)}
            style={{ ...inputStyle, marginBottom: 0 }}
          />
          {showSugestoes && sugestoesFiltradas.length > 0 && (
            <div style={{
              position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 10,
              background: "#0f1826", border: "1px solid #2e3555", borderRadius: 8,
              overflow: "hidden", boxShadow: "0 4px 16px rgba(0,0,0,0.5)",
            }}>
              {sugestoesFiltradas.map((s) => (
                <button
                  key={s}
                  onMouseDown={(e) => { e.preventDefault(); setPlano(s); setShowSugestoes(false); }}
                  style={{
                    display: "block", width: "100%", textAlign: "left", padding: "8px 10px",
                    background: "none", border: "none", cursor: "pointer", fontSize: 12.5,
                    color: "#f1f5f9",
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>

        <label style={{ fontSize: 11, color: "#94a3b8", display: "block", marginBottom: 4 }}>
          💰 Valor da venda (R$) <span style={{ color: "#64748b" }}>— preenche quando fechar</span>
        </label>
        <input type="text" inputMode="decimal" placeholder="Ex: 300.00" value={valor}
          onChange={(e) => setValor(e.target.value.replace(/[^0-9.,]/g, ""))} style={inputStyle} />

        <label style={{ fontSize: 11, color: "#94a3b8", display: "block", marginBottom: 4 }}>Observações (opcional)</label>
        <textarea
          placeholder="Ex: cliente pediu desconto, fechar até dia 25..."
          value={obs}
          onChange={(e) => setObs(e.target.value)}
          rows={3}
          style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }}
        />

        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: "9px 0", borderRadius: 8, border: "1px solid #2e3555",
              background: "transparent", color: "#94a3b8", fontSize: 13, fontWeight: 600, cursor: "pointer",
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            style={{
              flex: 1, padding: "9px 0", borderRadius: 8, border: "none",
              background: "#3b82f6", color: "#fff", fontSize: 13, fontWeight: 700,
              cursor: saving ? "default" : "pointer", opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? "Salvando..." : mode === "add" ? "Adicionar" : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function KanbanCard({ lead, onMove, onDelete, onChat, onEdit, onDragStart }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const hasWA = (lead.phone || "").replace(/\D/g, "").length >= 10;

  return (
    <div
      draggable
      onDragStart={(e) => { setDragging(true); onDragStart(lead.id); e.dataTransfer.effectAllowed = "move"; }}
      onDragEnd={() => setDragging(false)}
      style={{
        background: dragging ? "rgba(59,130,246,0.08)" : "var(--bg1, #131929)",
        border: `1px solid ${dragging ? "#3b82f6" : "var(--bg3, #2e3555)"}`,
        borderRadius: 10, padding: 12, marginBottom: 8,
        position: "relative", cursor: "grab",
        opacity: dragging ? 0.5 : 1, transition: "all 0.15s", userSelect: "none",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1, minWidth: 0 }}>
          <GripVertical size={12} color="var(--text2, #94a3b8)" style={{ flexShrink: 0, opacity: 0.5 }} />
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text1, #f1f5f9)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lead.name}</div>
          {lead.source === "agent" && (
            <span title="Encontrado e abordado pelo agente automático" style={{ fontSize: 11, flexShrink: 0 }}>🤖</span>
          )}
        </div>
        <button onClick={(e) => { e.stopPropagation(); setMenuOpen(o => !o); }}
          style={{ background: "none", border: "none", cursor: "pointer", padding: "2px 4px", color: "var(--text2, #94a3b8)", flexShrink: 0 }}>
          <ChevronDown size={14} />
        </button>
      </div>

      {lead.qualification_reason && (
        <div style={{ fontSize: 10.5, color: "#a78bfa", marginBottom: 6, fontStyle: "italic" }}>
          🧠 {lead.qualification_reason}
        </div>
      )}

      {lead.col === "perdido" && (
        <div style={{ fontSize: 10.5, color: "#71717a", marginBottom: 6, fontWeight: 600 }}>
          🔇 Bot não respondeu de propósito — lead recusou
        </div>
      )}

      {lead.needs_human && (
        <div style={{ fontSize: 10.5, color: "#f59e0b", marginBottom: 6, fontWeight: 600 }}>
          🙋 Bot chegou no limite — assuma essa conversa
        </div>
      )}

      {lead.phone && (
        <div style={{ fontSize: 11, color: "var(--text2, #94a3b8)", marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          📞 {lead.phone}
        </div>
      )}
      {lead.segment && (
        <div style={{ fontSize: 11, color: "var(--text2, #94a3b8)", marginBottom: 4 }}>🏷 {lead.segment}</div>
      )}
      {lead.cnpj && (
        <div style={{ fontSize: 11, color: "var(--text2, #94a3b8)", marginBottom: 4 }}>🧾 {lead.cnpj}</div>
      )}
      {lead.plano_negociado && (
        <div style={{ fontSize: 11, color: "#60a5fa", marginBottom: 4, fontWeight: 600 }}>📶 {lead.plano_negociado}</div>
      )}
      {lead.value > 0 && (
        <div style={{ fontSize: 11.5, color: "#22c55e", marginBottom: 4, fontWeight: 700 }}>💰 {fmtBRL(lead.value)}</div>
      )}
      {lead.observacoes && (
        <div style={{ fontSize: 10.5, color: "var(--text2, #94a3b8)", marginBottom: 4, fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          📝 {lead.observacoes}
        </div>
      )}

      {/* Botão WhatsApp no card */}
      {hasWA && (
        <button
          onClick={(e) => { e.stopPropagation(); onChat(lead); }}
          style={{
            display: "flex", alignItems: "center", gap: 5, marginTop: 6,
            padding: "5px 8px", borderRadius: 7, width: "100%",
            background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.25)",
            color: "#22c55e", fontSize: 11, fontWeight: 600, cursor: "pointer",
          }}
        >
          <MessageCircle size={11} /> Enviar WhatsApp
        </button>
      )}

      {/* Menu */}
      {menuOpen && (
        <div style={{ position: "absolute", top: 36, right: 8, zIndex: 20, background: "var(--bg2, #0f1826)", border: "1px solid var(--bg3, #2e3555)", borderRadius: 10, overflow: "hidden", minWidth: 160, boxShadow: "0 4px 20px rgba(0,0,0,0.4)" }}>
          <button onClick={() => { onEdit(lead); setMenuOpen(false); }}
            style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 12px", background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "var(--text1, #f1f5f9)", fontWeight: 500, borderBottom: "1px solid var(--bg3, #2e3555)" }}>
            ✎ Editar detalhes
          </button>
          <div style={{ padding: "8px 12px", fontSize: 11, color: "var(--text2, #94a3b8)", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}>Mover para</div>
          {KANBAN_COLS.filter(c => c.id !== lead.col).map(c => (
            <button key={c.id} onClick={() => { onMove(lead.id, c.id); setMenuOpen(false); }}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 12px", background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "var(--text1, #f1f5f9)", fontWeight: 500 }}>
              {c.icon} {c.label}
            </button>
          ))}
          <div style={{ borderTop: "1px solid var(--bg3, #2e3555)" }}>
            <button onClick={() => { onDelete(lead.id); setMenuOpen(false); }}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 12px", background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#ef4444", fontWeight: 500 }}>
              🗑 Remover
            </button>
          </div>
        </div>
      )}
      {menuOpen && <div style={{ position: "fixed", inset: 0, zIndex: 19 }} onClick={() => setMenuOpen(false)} />}
    </div>
  );
}

// ─── Dashboard do Pipeline ────────────────────────────────────
function PipelineDashboard({ leads, isDark }) {
  const total = leads.length;
  const fechados = leads.filter(l => l.col === "fechado");
  const perdidos = leads.filter(l => l.col === "perdido");
  const receitaFechada = fechados.reduce((s, l) => s + (l.value || 0), 0);
  const receitaPotencial = leads.filter(l => !["fechado","perdido"].includes(l.col)).reduce((s, l) => s + (l.value || 0), 0);
  const taxaConversao = total > 0 ? ((fechados.length / total) * 100).toFixed(1) : "0.0";

  const cards = [
    { label: "Total de Leads",    value: total,           icon: Target,       color: "#3b82f6" },
    { label: "Fechados",          value: fechados.length, icon: CheckCircle2, color: "#22c55e" },
    { label: "Perdidos",          value: perdidos.length, icon: XCircle,      color: "#ef4444" },
    { label: "Taxa de Conversão", value: `${taxaConversao}%`, icon: TrendingUp, color: "#f59e0b" },
  ];

  const barCols = KANBAN_COLS.map(c => ({
    ...c,
    count: leads.filter(l => l.col === c.id).length,
    pct: total > 0 ? (leads.filter(l => l.col === c.id).length / total) * 100 : 0,
  }));

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
        {cards.map(c => (
          <div key={c.label} style={{ background: isDark ? "#18181b" : "#fff", border: `1px solid ${c.color}25`, borderRadius: 14, padding: "12px 14px", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: c.color + "80" }} />
            <div style={{ width: 32, height: 32, borderRadius: 9, background: `${c.color}18`, border: `1px solid ${c.color}35`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 8 }}>
              <c.icon size={15} color={c.color} />
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: isDark ? "#f4f4f5" : "#18181b", letterSpacing: "-0.02em" }}>{c.value}</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: c.color, opacity: 0.8, marginTop: 1 }}>{c.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <div style={{ flex: 1, background: "linear-gradient(135deg, rgba(34,197,94,0.08), rgba(34,197,94,0.03))", border: "1px solid rgba(34,197,94,0.3)", borderRadius: 14, padding: "12px 14px" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#22c55e", marginBottom: 4 }}>💰 Receita Fechada</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#22c55e" }}>{fmtBRL(receitaFechada)}</div>
        </div>
        <div style={{ flex: 1, background: "linear-gradient(135deg, rgba(245,158,11,0.08), rgba(245,158,11,0.03))", border: "1px solid rgba(245,158,11,0.3)", borderRadius: 14, padding: "12px 14px" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#f59e0b", marginBottom: 4 }}>🎯 Potencial</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#f59e0b" }}>{fmtBRL(receitaPotencial)}</div>
        </div>
      </div>

      <div style={{ background: isDark ? "#18181b" : "#fff", border: "1px solid #3f3f46", borderRadius: 14, padding: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: isDark ? "#f4f4f5" : "#18181b", marginBottom: 14, textTransform: "uppercase", letterSpacing: "0.06em", opacity: 0.7 }}>Distribuição por estágio</div>
        {barCols.map(c => (
          <div key={c.id} style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: isDark ? "#a1a1aa" : "#52525b", display: "flex", alignItems: "center", gap: 5, fontWeight: 500 }}>{c.icon} {c.label}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: c.color }}>{c.count}</span>
            </div>
            <div style={{ height: 6, background: isDark ? "#27272a" : "#e4e4e7", borderRadius: 4, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${c.pct}%`, background: `linear-gradient(90deg, ${c.color}, ${c.color}aa)`, borderRadius: 4, transition: "width 0.5s ease" }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Aba Pipeline ─────────────────────────────────────────────
function PipelineTab({ leads, loading, onMove, onDelete, onOpenChat, onAddManual, onEditDetails, onBulkMoveLeadsToPerdido }) {
  const [draggingId, setDraggingId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingLead, setEditingLead] = useState(null);

  // Mesmo padrão do resto do arquivo: lê a classe "dark" do <html> pra
  // saber o tema atual, evitando texto claro-sobre-claro no modo Claro.
  const [isDark, setIsDark] = useState(
    () => document.documentElement.classList.contains("dark")
  );
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  const handleDragOver  = (e, colId) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setOverCol(colId); };
  const handleDrop      = (e, colId) => { e.preventDefault(); if (draggingId) onMove(draggingId, colId); setDraggingId(null); setOverCol(null); };
  const handleDragLeave = () => setOverCol(null);

  const qtdEmLead = leads.filter(l => l.col === "lead").length;

  return (
    <div>
      <PipelineDashboard leads={leads} isDark={isDark} />

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        {qtdEmLead > 0 && (
          <button
            onClick={onBulkMoveLeadsToPerdido}
            title="Move todos os cards da coluna Lead pra Perdido de uma vez"
            style={{
              display: "flex", alignItems: "center", gap: 6,
              background: "transparent", color: "#ef4444", border: "1px solid #3f3f46",
              borderRadius: 8, padding: "8px 14px", fontSize: 12.5, fontWeight: 700,
              cursor: "pointer",
            }}
          >
            <XCircle size={14} /> Mover {qtdEmLead} lead(s) pra Perdido
          </button>
        )}
        <button
          onClick={() => setShowAddModal(true)}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            background: "#3b82f6", color: "#fff", border: "none",
            borderRadius: 8, padding: "8px 14px", fontSize: 12.5, fontWeight: 700,
            cursor: "pointer",
          }}
        >
          <Plus size={14} /> Adicionar lead
        </button>
      </div>

      {showAddModal && (
        <LeadDetailsModal
          mode="add"
          initialValues={null}
          onClose={() => setShowAddModal(false)}
          onSubmit={onAddManual}
        />
      )}

      {editingLead && (
        <LeadDetailsModal
          mode="edit"
          initialValues={editingLead}
          onClose={() => setEditingLead(null)}
          onSubmit={(values) => onEditDetails(editingLead.id, values)}
        />
      )}

      {leads.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12, padding: "8px 12px", background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.15)", borderRadius: 8 }}>
          <GripVertical size={12} color="#3b82f6" />
          <span style={{ fontSize: 11, color: "#3b82f6", fontWeight: 500 }}>Arraste os cards entre as colunas ou use o menu ˅ — a IA também move automaticamente quando o lead responde 🤖</span>
        </div>
      )}

      {!loading && leads.length === 0 && (
        <div style={{ textAlign: "center", padding: "32px 16px", color: "#475569" }}>
          <Target size={32} style={{ margin: "0 auto 12px", opacity: 0.3 }} />
          <div style={{ fontSize: 14, fontWeight: 600, color: "#64748b", marginBottom: 4 }}>Pipeline vazio</div>
          <div style={{ fontSize: 12 }}>Vá na aba Busca e clique em "+ Pipeline", ou use o Agente pra preencher automaticamente</div>
        </div>
      )}

      <div style={{ overflowX: "auto", marginLeft: -16, marginRight: -16, paddingLeft: 16, paddingRight: 16, paddingBottom: 8 }}>
        <div style={{ display: "flex", gap: 10, minWidth: `${KANBAN_COLS.length * 220}px` }}>
          {KANBAN_COLS.map(col => {
            const colLeads = leads.filter(l => l.col === col.id);
            const isOver = overCol === col.id;
            return (
              <div key={col.id}
                onDragOver={(e) => handleDragOver(e, col.id)}
                onDrop={(e) => handleDrop(e, col.id)}
                onDragLeave={handleDragLeave}
                style={{
                  flex: "0 0 210px",
                  background: isOver ? `${col.color}12` : (isDark ? "#18181b" : "#fff"),
                  border: `1.5px solid ${isOver ? col.color : col.color + "30"}`,
                  borderRadius: 14, padding: 12, transition: "all 0.15s",
                  minHeight: 140, position: "relative", overflow: "hidden",
                }}
              >
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${col.color}cc, ${col.color}44)` }} />
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, marginTop: 4 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 15 }}>{col.icon}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: col.color }}>{col.label}</span>
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 20, background: `${col.color}20`, color: col.color, border: `1px solid ${col.color}40`, minWidth: 22, textAlign: "center" }}>
                    {colLeads.length}
                  </div>
                </div>

                {colLeads.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "24px 8px", color: isOver ? col.color : (isDark ? "#a1a1aa" : "#52525b"), fontSize: 12, opacity: isOver ? 1 : 0.4, border: `2px dashed ${isOver ? col.color : col.color + "40"}`, borderRadius: 10, transition: "all 0.15s" }}>
                    {isOver ? "Solte aqui!" : "Nenhum lead"}
                  </div>
                ) : (
                  colLeads.map(lead => (
                    <KanbanCard key={lead.id} lead={lead} onMove={onMove} onDelete={onDelete}
                      onChat={(l) => onOpenChat({ name: l.name, phone: l.phone })}
                      onEdit={setEditingLead}
                      onDragStart={setDraggingId} />
                  ))
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Aba Agente ───────────────────────────────────────────────
function AgentTab({ onDone }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPro     = !user?.is_admin && (user?.plan_key === "pro" || user?.plan_key === "starter");
  const proLimit  = user?.plan_limits?.prospect_leads_per_month ?? 500;
  const [monthlyUsed, setMonthlyUsed] = useState(null);

  useEffect(() => {
    if (isPro) {
      api.get("/prospect/stats").then(({ data }) => setMonthlyUsed(data.used ?? data.used_this_month ?? 0)).catch(() => {});
    }
  }, [isPro]);

  const [cidade,   setCidade]   = useState("");
  const [cidadeSugs,    setCidadeSugs]    = useState([]);
  const [cidadeLoading, setCidadeLoading] = useState(false);
  const cidadeTimer = useRef(null);
  const [bairro,   setBairro]   = useState("");
  const [bairrosList, setBairrosList] = useState([]);
  const [bairroInput, setBairroInput] = useState("");
  const [autoFillingBairros, setAutoFillingBairros] = useState(false);

  const handleAutoFillBairros = async () => {
    const cidadeNome = cidade.split(" — ")[0].trim();
    if (!cidadeNome) { toast.error("Preencha a cidade primeiro."); return; }
    setAutoFillingBairros(true);
    try {
      const r = await fetch("https://servicodados.ibge.gov.br/api/v1/localidades/municipios?orderBy=nome");
      const municipios = await r.json();
      const mun = municipios.find(m => m.nome.toLowerCase() === cidadeNome.toLowerCase());
      if (!mun) { toast.error("Não achei essa cidade no IBGE — adicione os bairros manualmente."); return; }
      const r2 = await fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/municipios/${mun.id}/distritos`);
      const distritos = await r2.json();
      const novos = distritos
        .map(d => d.nome)
        .filter(nome => nome.toLowerCase() !== cidadeNome.toLowerCase() && !bairrosList.includes(nome))
        .slice(0, 5);
      if (novos.length === 0) {
        toast.info("Não achei bairros/distritos novos pra essa cidade no IBGE — adicione manualmente.");
        return;
      }
      setBairrosList(prev => [...prev, ...novos]);
      toast.success(`${novos.length} bairro(s) adicionado(s) automaticamente!`);
    } catch {
      toast.error("Erro ao buscar bairros no IBGE. Tente adicionar manualmente.");
    } finally { setAutoFillingBairros(false); }
  };
  const [segmento, setSegmento] = useState("");
  const [segCustom, setSegCustom] = useState("");

  // Mesmo padrão do App.js/MailingTab: lê a classe "dark" do <html> pra
  // saber o tema atual, evitando texto claro-sobre-claro no modo Claro.
  const [isDark, setIsDark] = useState(
    () => document.documentElement.classList.contains("dark")
  );
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  // ── Modo "buscar por endereço" (CEP + número) — só vale pra rodada manual ──
  const [modoAgente, setModoAgente] = useState("bairro"); // "bairro" | "endereco"
  const [cepInput, setCepInput] = useState("");
  const [numeroInput, setNumeroInput] = useState("");
  const [enderecosList, setEnderecosList] = useState([]);
  const [cepPreview, setCepPreview] = useState(null);
  const [cepPreviewLoading, setCepPreviewLoading] = useState(false);
  const cepPreviewTimer = useRef(null);
  const cepPreviewHideTimer = useRef(null);

  useEffect(() => {
    clearTimeout(cepPreviewTimer.current);
    clearTimeout(cepPreviewHideTimer.current);
    const cepLimpo = cepInput.replace(/\D/g, "");
    if (cepLimpo.length !== 8) { setCepPreview(null); return; }
    cepPreviewTimer.current = setTimeout(async () => {
      setCepPreviewLoading(true);
      try {
        const { data } = await api.get("/cep/lookup", { params: { cep: cepLimpo } });
        setCepPreview(data);
        cepPreviewHideTimer.current = setTimeout(() => setCepPreview(null), 10000);
      } catch {
        setCepPreview({ erro: true });
        cepPreviewHideTimer.current = setTimeout(() => setCepPreview(null), 10000);
      } finally { setCepPreviewLoading(false); }
    }, 400);
    return () => clearTimeout(cepPreviewTimer.current);
  }, [cepInput]);

  const adicionarEnderecoAgente = () => {
    const cepLimpo = cepInput.replace(/\D/g, "");
    if (cepLimpo.length !== 8) { toast.error("CEP inválido — precisa ter 8 dígitos."); return; }
    if (enderecosList.some(e => e.cep === cepLimpo)) { toast.error("Esse CEP já foi adicionado."); return; }
    setEnderecosList(prev => [...prev, {
      cep: cepLimpo, numero: numeroInput.trim(),
      preview: cepPreview && !cepPreview.erro ? `${cepPreview.logradouro}${numeroInput.trim() ? ", " + numeroInput.trim() : ""} — ${cepPreview.bairro}` : cepLimpo,
    }]);
    setCepInput(""); setNumeroInput(""); setCepPreview(null);
  };

  const removerEnderecoAgente = (cep) => setEnderecosList(prev => prev.filter(e => e.cep !== cep));
  const [maxLeads, setMaxLeads] = useState(50);
  const [message,  setMessage]  = useState(AGENT_DEFAULT_MESSAGE);
  const [negotiationPrompt, setNegotiationPrompt] = useState("");
  const [offersBookFilename, setOffersBookFilename] = useState("");
  const [uploadingBook, setUploadingBook] = useState(false);
  const bookFileRef = useRef(null);

  const handleUploadBook = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // permite selecionar o mesmo arquivo de novo depois
    if (!file) return;

    const ext = file.name.toLowerCase().split(".").pop();
    if (!["pdf", "docx"].includes(ext)) {
      toast.error("Só aceita PDF ou Word (.docx).");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Arquivo muito grande — máximo 8MB.");
      return;
    }

    setUploadingBook(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/prospect/agent/upload-book", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setOffersBookFilename(data.filename);
      toast.success(`📎 Book "${data.filename}" processado! A IA já pode usar essas ofertas na conversa.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao processar o arquivo.");
    } finally { setUploadingBook(false); }
  };

  const handleRemoveBook = async () => {
    try {
      await api.delete("/prospect/agent/book");
      setOffersBookFilename("");
      toast.success("Book removido.");
    } catch { toast.error("Erro ao remover o book."); }
  };
  const [operadoras, setOperadoras] = useState([]);       // ["claro","vivo","tim"]
  const [modoPessoa, setModoPessoa] = useState("pj");     // "pf" | "pj" — vale pra todas
  // Derivado, não estado: mantém funcionando todo o código existente que
  // checa "sou revendedor Claro" (validações, textos condicionais etc).
  const useClaroBook = operadoras.includes("claro");
  const [useGif, setUseGif] = useState(false);
  const [offerImageUrl, setOfferImageUrl] = useState("");
  const [uploadingOfferImage, setUploadingOfferImage] = useState(false);
  const offerImageFileRef = useRef(null);

  const handleUploadOfferImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // permite selecionar o mesmo arquivo de novo depois
    if (!file) return;

    const ext = file.name.toLowerCase().split(".").pop();
    if (!["jpg", "jpeg", "png", "webp"].includes(ext)) {
      toast.error("Só aceita JPG, PNG ou WEBP.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Arquivo muito grande — máximo 10MB.");
      return;
    }

    setUploadingOfferImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/prospect/agent/upload-offer-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setOfferImageUrl(data.offer_image_url);
      toast.success("🖼️ Imagem de ofertas atualizada! Já entra no próximo envio com GIF/imagem ativado.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar a imagem.");
    } finally { setUploadingOfferImage(false); }
  };
  const [running,  setRunning]  = useState(false);
  const [run,       setRun]      = useState(null);
  const [todayInfo, setTodayInfo] = useState(null);
  const [autopilotOn, setAutopilotOn] = useState(false);
  const [botEnabled, setBotEnabled] = useState(true);
  const [configLoading, setConfigLoading] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);
  const [testingCycle, setTestingCycle] = useState(false);

  // Palpites de cidade (IBGE), mesmo esquema da aba Busca
  useEffect(() => {
    clearTimeout(cidadeTimer.current);
    if (cidade.length < 2 || running) { setCidadeSugs([]); return; }
    cidadeTimer.current = setTimeout(async () => {
      setCidadeLoading(true);
      try {
        const r = await fetch("https://servicodados.ibge.gov.br/api/v1/localidades/municipios?orderBy=nome");
        const data = await r.json();
        const q = cidade.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        const matches = data
          .filter(m => m.nome.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").startsWith(q))
          .slice(0, 8)
          .map(m => `${m.nome} — ${m.microrregiao?.mesorregiao?.UF?.sigla || ""}`);
        setCidadeSugs(matches);
      } catch { setCidadeSugs([]); }
      finally { setCidadeLoading(false); }
    }, 300);
  }, [cidade, running]);
  const pollRef = useRef(null);

  const fetchToday = useCallback(() => {
    api.get("/prospect/agent/today").then(({ data }) => setTodayInfo(data)).catch(() => {});
  }, []);

  // Carrega a configuração salva (e o estado do piloto automático) ao abrir a aba
  useEffect(() => {
    fetchToday();
    api.get("/prospect/agent/config").then(({ data }) => {
      setCidade(data.cidade || "");
      setBairro(data.bairro || "");
      setBairrosList(data.bairros_list || []);
      const isKnownSeg = SEGMENTOS.some(s => s.value === data.query);
      if (isKnownSeg) { setSegmento(data.query); setSegCustom(""); }
      else if (data.query) { setSegCustom(data.query); setSegmento(""); }
      setMaxLeads(data.daily_limit || 50);
      setMessage(data.message_template || AGENT_DEFAULT_MESSAGE);
      setNegotiationPrompt(data.negotiation_prompt || "");
      setOperadoras(
        Array.isArray(data.operadoras) && data.operadoras.length
          ? data.operadoras
          : (data.use_claro_book ? ["claro"] : [])
      );
      setModoPessoa(data.modo_pessoa === "pf" ? "pf" : "pj");
      setUseGif(!!data.use_gif);
      setOfferImageUrl(data.offer_image_url || "");
      setOffersBookFilename(data.offers_book_filename || "");
      setBotEnabled(data.bot_enabled !== false);
      setAutopilotOn(!!data.enabled);
      setModoAgente(data.modo_agente === "endereco" ? "endereco" : "bairro");
      setEnderecosList((data.ceps_list || []).map(e => ({
        cep: e.cep, numero: e.numero || "",
        preview: e.numero ? `${e.cep} — nº ${e.numero}` : e.cep,
      })));
    }).catch(() => {}).finally(() => setConfigLoading(false));

    // Reconecta sozinho com uma rodada manual que já estava rolando —
    // trocar de aba desmonta esse componente e zera o estado local, mas o
    // envio no servidor continua rodando por trás. Sem isso, ao voltar
    // pra aba parecia que "tinha zerado", quando só perdeu o acompanhamento.
    api.get("/prospect/agent/current-run").then(({ data }) => {
      if (data.run) {
        setRun(data.run);
        setRunning(true);
        pollRun(data.run.id);
      }
    }).catch(() => {});

    return () => clearInterval(pollRef.current);
  }, [fetchToday]);

  // Salva a lista de CEPs (e o modo escolhido) sozinho, assim que muda —
  // sem isso, só ficava na memória local e resetava toda vez que o usuário
  // trocava de aba (o componente é desmontado e o estado local se perde).
  useEffect(() => {
    if (configLoading) return; // não salva por cima antes do carregamento inicial terminar
    api.post("/prospect/agent/config", { ...buildConfigPayload(autopilotOn) }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enderecosList, modoAgente]);

  const buildConfigPayload = (enabled) => {
    const term = segCustom.trim() || segmento;
    const cidadeNome = cidade.split(" — ")[0].trim();
    return {
      enabled,
      query: term,
      cidade: cidadeNome,
      bairro: bairro.trim(),
      message_template: message.trim() || AGENT_DEFAULT_MESSAGE,
      negotiation_prompt: negotiationPrompt.trim(),
      use_claro_book: useClaroBook,
      operadoras,
      modo_pessoa: modoPessoa,
      use_gif: useGif,
      daily_limit: maxLeads,
      bairros_list: bairrosList,
      bot_enabled: botEnabled,
      ceps_list: enderecosList.map(e => ({ cep: e.cep, numero: e.numero })),
      modo_agente: modoAgente,
    };
  };

  const handleToggleBot = async () => {
    const next = !botEnabled;
    if (!next && autopilotOn) {
      toast.error("Desliga o Piloto Automático primeiro — ele precisa da IA de Prospecção ligada pra responder os leads que ele mesmo contata.");
      return;
    }
    setBotEnabled(next);
    setSavingConfig(true);
    try {
      await api.post("/prospect/agent/config", { ...buildConfigPayload(autopilotOn), bot_enabled: next });
      toast.success(next ? "IA de Prospecção ativada 🤖" : `IA de Prospecção desativada — leads${useClaroBook ? "" : " desse segmento"} não recebem resposta automática nenhuma.`);
    } catch (e) {
      setBotEnabled(!next);
      toast.error(e?.response?.data?.detail || "Não foi possível salvar a configuração.");
    } finally {
      setSavingConfig(false);
    }
  };

  const handleToggleAutopilot = async () => {
    const next = !autopilotOn;
    if (next && !botEnabled) {
      toast.error("Ative a IA de Prospecção primeiro — sem ela, o agente contata os leads mas ninguém responde.");
      return;
    }
    if (next && modoAgente === "endereco") {
      toast.error("O Piloto Automático usa rotação de bairros — troca pro modo 'Por bairro/segmento' antes de ativar.");
      return;
    }
    if (next && (!(segCustom.trim() || segmento) || !cidade.split(" — ")[0].trim())) {
      toast.error("Preencha cidade e segmento antes de ativar o piloto automático.");
      return;
    }
    if (next && !negotiationPrompt.trim() && !useClaroBook && !offersBookFilename) {
      toast.error("Defina o prompt de negociação antes de ativar o piloto automático.");
      return;
    }
    setAutopilotOn(next);
    setSavingConfig(true);
    try {
      await api.post("/prospect/agent/config", buildConfigPayload(next));
      toast.success(next ? "Piloto automático ativado 🚀" : "Piloto automático desativado");
    } catch (e) {
      setAutopilotOn(!next);
      toast.error(e?.response?.data?.detail || "Não foi possível salvar a configuração.");
    } finally {
      setSavingConfig(false);
    }
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      await api.post("/prospect/agent/config", buildConfigPayload(autopilotOn));
      toast.success("Configuração salva!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível salvar a configuração.");
    } finally {
      setSavingConfig(false);
    }
  };

  const handleTestCycle = async () => {
    setTestingCycle(true);
    try {
      await api.post("/prospect/agent/autopilot/test-cycle");
      toast.success("Ciclo testado! Confere o resultado no Pipeline em alguns segundos.");
      setTimeout(fetchToday, 4000);
      if (onDone) setTimeout(onDone, 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível testar o ciclo.");
    } finally {
      setTestingCycle(false);
    }
  };

  const [stopping, setStopping] = useState(false);

  const handleStopRun = async () => {
    const runId = run?.run_id || run?.id;
    if (!runId) return;
    setStopping(true);
    try {
      await api.post(`/prospect/agent/runs/${runId}/stop`);
      toast.info("Parando a rodada... vai finalizar assim que o envio atual (se houver) terminar.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao tentar parar a rodada.");
    } finally { setStopping(false); }
  };

  const pollRun = (runId) => {
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/prospect/agent/runs/${runId}`);
        setRun(data);
        if (data.status !== "running") {
          clearInterval(pollRef.current);
          setRunning(false);
          fetchToday();
          if (onDone) onDone();
        }
      } catch (e) {
        clearInterval(pollRef.current);
        setRunning(false);
      }
    }, 2000);
  };

  const handleRun = async () => {
    const term = segCustom.trim() || segmento;
    const cidadeNome = cidade.split(" — ")[0].trim();
    if (modoAgente === "endereco") {
      if (enderecosList.length === 0) { toast.error("Adicione pelo menos um CEP."); return; }
    } else {
      if (!term || !cidadeNome) { toast.error("Preencha a cidade e o segmento"); return; }
    }
    if (!negotiationPrompt.trim() && !useClaroBook && !offersBookFilename) { toast.error("Defina o prompt de negociação, anexe um book de ofertas, ou marque o Claro Book antes de rodar."); return; }
    setRunning(true);
    setRun(null);
    try {
      // Salva a config primeiro (incluindo o prompt de negociação), pra que
      // a conversa pós-resposta do lead já saiba como negociar nesse segmento
      // mesmo numa rodada manual (sem o piloto automático ativado).
      await api.post("/prospect/agent/config", { ...buildConfigPayload(autopilotOn) });
      const payload = {
        query: term,
        cidade: cidadeNome,
        max_leads: maxLeads,
        message_template: message.trim() || AGENT_DEFAULT_MESSAGE,
      };
      if (modoAgente === "endereco") {
        payload.ceps = enderecosList.map(e => ({ cep: e.cep, numero: e.numero }));
      } else {
        payload.bairro = bairro.trim();
      }
      const { data } = await api.post("/prospect/agent/run", payload);
      setRun(data);
      if (data.status === "running") {
        pollRun(data.run_id);
      } else {
        setRunning(false);
        fetchToday();
        if (onDone) onDone();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao rodar o agente. Tente novamente.");
      setRunning(false);
    }
  };

  return (
    <div>
      {/* ── Banner aviso Plano Pro ── */}
      {isPro && (
        <div style={{ marginBottom: 18, background: "rgba(245,158,11,0.07)", border: "1px solid rgba(245,158,11,0.35)", borderRadius: 12, padding: "14px 16px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <AlertTriangle style={{ color: "#f59e0b", flexShrink: 0, marginTop: 1 }} size={18} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#fcd34d", marginBottom: 4 }}>
                Atenção: Plano Pro tem limite de {proLimit} leads/mês
              </div>
              <p style={{ fontSize: 12, color: "#a1a1aa", margin: "0 0 10px 0", lineHeight: 1.6 }}>
                No ritmo máximo de <strong style={{ color: isDark ? "#f4f4f5" : "#18181b" }}>50 leads/dia</strong>, você esgota os {proLimit} leads em apenas{" "}
                <strong style={{ color: "#fbbf24" }}>{Math.floor(proLimit / 50)} dias corridos</strong> — e o agente para de funcionar pelo resto do mês.
                {monthlyUsed !== null && (
                  <> Você já usou <strong style={{ color: monthlyUsed >= proLimit * 0.8 ? "#f87171" : (isDark ? "#f4f4f5" : "#18181b") }}>{monthlyUsed} de {proLimit}</strong> leads este mês.</>
                )}
              </p>

              {/* Barra de uso mensal */}
              {monthlyUsed !== null && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#71717a", marginBottom: 4 }}>
                    <span>Uso mensal</span>
                    <span style={{ color: monthlyUsed >= proLimit ? "#f87171" : "#a1a1aa" }}>
                      {Math.min(100, Math.round((monthlyUsed / proLimit) * 100))}% usado
                    </span>
                  </div>
                  <div style={{ height: 6, borderRadius: 100, background: "rgba(255,255,255,0.08)", overflow: "hidden" }}>
                    <div style={{
                      height: "100%", borderRadius: 100, transition: "width 0.4s ease",
                      width: `${Math.min(100, Math.round((monthlyUsed / proLimit) * 100))}%`,
                      background: monthlyUsed >= proLimit * 0.8
                        ? "linear-gradient(90deg, #f59e0b, #ef4444)"
                        : "linear-gradient(90deg, #f59e0b, #fbbf24)",
                    }} />
                  </div>
                </div>
              )}

              {/* Comparativo Agência */}
              <div style={{ background: "rgba(167,139,250,0.08)", border: "1px solid rgba(167,139,250,0.2)", borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#c4b5fd", marginBottom: 4 }}>
                  💡 Por que o Plano Agência vale mais a pena?
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: 11.5 }}>
                  <div style={{ color: "#a1a1aa" }}>
                    <div style={{ color: "#71717a", fontSize: 10, fontWeight: 600, marginBottom: 2 }}>PLANO PRO (atual)</div>
                    <div>• {proLimit} leads/mês</div>
                    <div>• Para em {Math.floor(proLimit / 50)} dias</div>
                    <div>• R$ 49/mês</div>
                  </div>
                  <div style={{ color: "#c4b5fd" }}>
                    <div style={{ color: "#a78bfa", fontSize: 10, fontWeight: 600, marginBottom: 2 }}>PLANO AGÊNCIA</div>
                    <div>• <strong>2.000 leads/mês</strong></div>
                    <div>• <strong>Mês inteiro sem parar</strong></div>
                    <div>• R$ 149/mês</div>
                  </div>
                </div>
              </div>

              <button
                onClick={() => navigate("/planos")}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(167,139,250,0.2)", border: "1px solid rgba(167,139,250,0.5)", borderRadius: 8, padding: "7px 14px", fontSize: 12.5, fontWeight: 600, color: "#a78bfa", cursor: "pointer" }}>
                <Sparkles size={14} /> Fazer upgrade para o Plano Agência
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ background: "linear-gradient(135deg, rgba(167,139,250,0.08), rgba(96,165,250,0.04))", border: "1px solid rgba(167,139,250,0.25)", borderRadius: 14, padding: 16, marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 10, paddingBottom: 10, borderBottom: "1px solid rgba(167,139,250,0.15)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 18 }}>💬</span>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: (isDark ? "#f4f4f5" : "#18181b") }}>IA de Prospecção</div>
              <div style={{ fontSize: 11, color: (isDark ? "#a1a1aa" : "#52525b") }}>Responde automaticamente quem chamar seus leads de prospecção</div>
            </div>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: configLoading ? "default" : "pointer", opacity: configLoading ? 0.5 : 1, flexShrink: 0 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: botEnabled ? "#4ade80" : (isDark ? "#a1a1aa" : "#52525b") }}>
              {botEnabled ? "Ativado" : "Desativado"}
            </span>
            <div onClick={() => !configLoading && !savingConfig && handleToggleBot()}
              className={`relative w-9 h-5 rounded-full transition-colors ${configLoading ? "" : "cursor-pointer"} ${botEnabled ? "bg-green-500" : "bg-zinc-700"}`}>
              <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${botEnabled ? "translate-x-4" : "translate-x-0"}`} />
            </div>
          </label>
        </div>
        {!botEnabled && (
          <div style={{ fontSize: 11.5, color: "#f59e0b", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
            <AlertTriangle size={13} /> IA de Prospecção desligada: nenhum lead recebe resposta automática, nem quando responder. Você atende manualmente pelo chat.
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 18 }}>🤖</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: (isDark ? "#f4f4f5" : "#18181b") }}>Agente de Prospecção</span>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: configLoading ? "default" : "pointer", opacity: configLoading ? 0.5 : 1 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: autopilotOn ? "#a78bfa" : (isDark ? "#a1a1aa" : "#52525b") }}>
              Piloto automático {autopilotOn ? "ON" : "OFF"}
            </span>
            <div onClick={() => !configLoading && !savingConfig && handleToggleAutopilot()}
              className={`relative w-9 h-5 rounded-full transition-colors ${configLoading ? "" : "cursor-pointer"} ${autopilotOn ? "bg-purple-500" : "bg-zinc-700"}`}>
              <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${autopilotOn ? "translate-x-4" : "translate-x-0"}`} />
            </div>
          </label>
        </div>
        <p style={{ fontSize: 12, color: (isDark ? "#a1a1aa" : "#52525b"), margin: 0 }}>
          {autopilotOn
            ? "Ativado: o agente busca e aborda sozinho, espalhando o limite diário ao longo do dia (8h–20h), sem precisar clicar em nada."
            : "Desativado: use o botão \"Buscar e Abordar\" abaixo pra rodar manualmente, ou ative o piloto automático pra deixar rodando sozinho."}
        </p>
        {todayInfo && (
          <div style={{ marginTop: 10, fontSize: 11.5, color: (isDark ? "#a1a1aa" : "#52525b") }}>
            Hoje: <strong style={{ color: (isDark ? "#f4f4f5" : "#18181b") }}>{todayInfo.sent_today}</strong> de {todayInfo.daily_cap} contatados pelo agente
            {" "}({todayInfo.remaining_today} restantes — teto de segurança pra não arriscar bloqueio do número)
            {(() => {
              const pct = todayInfo.daily_cap > 0
                ? Math.min(100, Math.round((todayInfo.sent_today / todayInfo.daily_cap) * 100))
                : 0;
              return (
                <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#a78bfa", minWidth: 34 }}>{pct}%</span>
                  <div style={{ flex: 1, height: 7, borderRadius: 100, background: "rgba(255,255,255,0.08)", overflow: "hidden" }}>
                    <div
                      style={{
                        height: "100%",
                        width: `${pct}%`,
                        borderRadius: 100,
                        background: "linear-gradient(90deg, #a78bfa, #818cf8)",
                        transition: "width 0.4s ease",
                      }}
                    />
                  </div>
                  <span style={{ fontSize: 11, color: (isDark ? "#a1a1aa" : "#52525b"), whiteSpace: "nowrap" }}>
                    {todayInfo.sent_today} leads enviados / {todayInfo.daily_cap}
                  </span>
                </div>
              );
            })()}
          </div>
        )}
        {autopilotOn && (
          <button onClick={handleTestCycle} disabled={testingCycle}
            style={{ marginTop: 10, fontSize: 11.5, fontWeight: 600, color: "#a78bfa", background: "rgba(167,139,250,0.1)", border: "1px solid rgba(167,139,250,0.3)", borderRadius: 8, padding: "5px 10px", cursor: testingCycle ? "default" : "pointer" }}>
            {testingCycle ? "Testando..." : "🧪 Testar 1 ciclo agora (sem esperar 20 min)"}
          </button>
        )}
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={() => setModoAgente("bairro")}
            disabled={running}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${modoAgente === "bairro" ? "bg-blue-600 text-white" : "bg-zinc-800 text-zinc-400 hover:text-zinc-200"}`}
          >
            📍 Por bairro / segmento
          </button>
          <button
            onClick={() => setModoAgente("endereco")}
            disabled={running}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${modoAgente === "endereco" ? "bg-blue-600 text-white" : "bg-zinc-800 text-zinc-400 hover:text-zinc-200"}`}
          >
            🏢 Por endereço (CEP)
          </button>
          {modoAgente === "endereco" && (
            <span className="text-xs text-zinc-500 ml-1">— só vale pra rodada manual (o Piloto Automático usa o modo por bairro)</span>
          )}
        </div>

        {modoAgente === "bairro" ? (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-xs font-medium text-zinc-400 mb-1.5 block">Cidade</label>
            <AutocompleteInput
              value={cidade}
              onChange={running ? () => {} : setCidade}
              onSelect={running ? () => {} : setCidade}
              suggestions={running ? [] : cidadeSugs}
              loading={cidadeLoading}
              placeholder="Ex: São Paulo"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-400 mb-1.5 block">
              Bairro <span className="text-zinc-600">(só para rodada manual)</span>
            </label>
            <Input placeholder="Ex: Vila Mariana" value={bairro} onChange={e => setBairro(e.target.value)}
              className="bg-zinc-800 border-zinc-700 text-zinc-100" disabled={running} />
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-400 mb-1.5 block">Segmento</label>
            <Select value={segmento} onValueChange={v => { setSegmento(v); setSegCustom(""); }} disabled={running}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-100"><SelectValue placeholder="Selecione..." /></SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                {SEGMENTOS.map(s => <SelectItem key={s.value} value={s.value} className="text-zinc-100">{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-400 mb-1.5 block">Ou busca livre</label>
            <Input placeholder="clínica odontológica..." value={segCustom} onChange={e => { setSegCustom(e.target.value); setSegmento(""); }}
              className="bg-zinc-800 border-zinc-700 text-zinc-100" disabled={running} />
          </div>
        </div>

        {/* ── Rotação de Bairros (Piloto Automático) ── */}
        <div style={{ marginBottom: 14, background: "rgba(167,139,250,0.06)", border: "1px solid rgba(167,139,250,0.2)", borderRadius: 10, padding: "12px 14px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13 }}>🔄</span>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--text1, #f4f4f5)" }}>Rotação de Bairros</span>
            <span style={{ fontSize: 10, background: "rgba(167,139,250,0.25)", color: "#a78bfa", borderRadius: 20, padding: "2px 8px", fontWeight: 600 }}>🤖 Piloto Automático</span>
            <button
              onClick={handleAutoFillBairros}
              disabled={running || autoFillingBairros || !cidade}
              title="Preenche automaticamente com bairros/distritos da cidade (via IBGE)"
              style={{
                marginLeft: "auto", display: "flex", alignItems: "center", gap: 5,
                padding: "4px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                background: !cidade ? "rgba(255,255,255,0.04)" : "rgba(34,197,94,0.15)",
                border: "1px solid", borderColor: !cidade ? "#3f3f46" : "rgba(34,197,94,0.4)",
                color: !cidade ? "#52525b" : "#22c55e",
                cursor: (running || autoFillingBairros || !cidade) ? "default" : "pointer",
              }}
            >
              {autoFillingBairros ? <Loader size={11} className="animate-spin" /> : "⚡"} Preencher automático
            </button>
          </div>
          <p style={{ fontSize: 11.5, color: "#a1a1aa", margin: "0 0 10px 0", lineHeight: 1.6 }}>
            O Google Maps retorna <strong style={{ color: isDark ? "#f4f4f5" : "#18181b" }}>no máximo 20 empresas por busca</strong>. Sem rotação, o agente esgota esses 20 leads rapidamente e fica parado o resto do dia.{" "}
            <strong style={{ color: "#c4b5fd" }}>Adicione bairros abaixo</strong> para o agente alternar entre eles a cada ciclo de 20 min e conseguir contatar até <strong style={{ color: "#a78bfa" }}>50 leads por dia</strong> — sem repetir nenhum contato.
          </p>

          {/* Tags dos bairros */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8, minHeight: 28 }}>
            {bairrosList.length === 0 ? (
              <span style={{ fontSize: 11.5, color: "#71717a", fontStyle: "italic", alignSelf: "center" }}>
                ⚠️ Nenhum bairro adicionado — o agente vai buscar sem filtro de bairro e atingirá bem menos de 50 leads/dia.
              </span>
            ) : bairrosList.map((b, i) => (
              <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "rgba(167,139,250,0.15)", border: "1px solid rgba(167,139,250,0.35)", borderRadius: 20, padding: "4px 12px", fontSize: 12, color: "#c4b5fd", fontWeight: 500 }}>
                📍 {b}
                <button
                  onClick={() => setBairrosList(prev => prev.filter((_, idx) => idx !== i))}
                  disabled={running}
                  title="Remover bairro"
                  style={{ background: "none", border: "none", color: "#7c6fae", cursor: running ? "default" : "pointer", fontSize: 16, lineHeight: 1, padding: "0 0 1px 2px", display: "flex", alignItems: "center" }}>
                  ×
                </button>
              </span>
            ))}
          </div>

          {/* Input para adicionar bairro */}
          <div style={{ display: "flex", gap: 6 }}>
            <input
              placeholder="Digite o nome do bairro e clique em Adicionar (ex: Pinheiros)"
              value={bairroInput}
              onChange={e => setBairroInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  const val = bairroInput.trim();
                  if (val && !bairrosList.includes(val)) {
                    setBairrosList(prev => [...prev, val]);
                    setBairroInput("");
                  }
                }
              }}
              disabled={running}
              style={{ flex: 1, background: "#27272a", border: "1px solid #3f3f46", borderRadius: 7, padding: "7px 11px", fontSize: 12.5, color: "#f4f4f5", outline: "none" }}
            />
            <button
              onClick={() => {
                const val = bairroInput.trim();
                if (val && !bairrosList.includes(val)) {
                  setBairrosList(prev => [...prev, val]);
                  setBairroInput("");
                }
              }}
              disabled={running || !bairroInput.trim()}
              style={{ padding: "7px 16px", background: bairroInput.trim() ? "rgba(167,139,250,0.2)" : "rgba(255,255,255,0.04)", border: "1px solid", borderColor: bairroInput.trim() ? "rgba(167,139,250,0.5)" : "#3f3f46", borderRadius: 7, fontSize: 12.5, color: bairroInput.trim() ? "#a78bfa" : "#52525b", cursor: (running || !bairroInput.trim()) ? "default" : "pointer", fontWeight: 600, transition: "all 0.15s", whiteSpace: "nowrap" }}>
              + Adicionar
            </button>
          </div>

          {/* Indicador de cobertura */}
          {bairrosList.length > 0 && (
            <div style={{ marginTop: 9, fontSize: 11.5, color: "#a1a1aa", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <span>📊 <strong style={{ color: isDark ? "#f4f4f5" : "#18181b" }}>{bairrosList.length} {bairrosList.length === 1 ? "bairro" : "bairros"}</strong> configurados</span>
              <span style={{ color: "#3f3f46" }}>•</span>
              <span>pool de até <strong style={{ color: "#a78bfa" }}>~{bairrosList.length * 20} leads únicos</strong> por rotação completa</span>
              {bairrosList.length >= 3
                ? <span style={{ color: "#22c55e", fontWeight: 600 }}>✅ Suficiente para 50 leads/dia</span>
                : <span style={{ color: "#f59e0b", fontWeight: 600 }}>⚠️ Adicione pelo menos {3 - bairrosList.length} bairro{3 - bairrosList.length > 1 ? "s" : ""} para cobrir 50/dia</span>}
            </div>
          )}
        </div>
        </>
        ) : (
        <div className="mb-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end mb-1">
            <div className="relative">
              <label className="text-xs font-medium text-zinc-400 mb-1.5 block">CEP</label>
              <Input placeholder="Ex: 01310-100" value={cepInput}
                onChange={e => setCepInput(e.target.value.replace(/[^\d-]/g, ""))}
                onKeyDown={e => e.key === "Enter" && adicionarEnderecoAgente()}
                disabled={running}
                className="bg-zinc-800 border-zinc-700 text-zinc-100" />
              {(cepPreviewLoading || cepPreview) && (
                <div style={{ fontSize: 11.5, marginTop: 5, color: cepPreview?.erro ? "#ef4444" : "#4ade80" }}>
                  {cepPreviewLoading ? "Consultando CEP..." : cepPreview.erro ? "CEP não encontrado." : `📍 ${cepPreview.logradouro} — ${cepPreview.bairro}, ${cepPreview.cidade}/${cepPreview.uf}`}
                </div>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-400 mb-1.5 block">Número <span className="text-zinc-600">(opcional)</span></label>
              <Input placeholder="Ex: 1000" value={numeroInput} onChange={e => setNumeroInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && adicionarEnderecoAgente()}
                disabled={running}
                className="bg-zinc-800 border-zinc-700 text-zinc-100" />
            </div>
            <Button onClick={adicionarEnderecoAgente} disabled={running} variant="outline" className="border-zinc-700 text-zinc-200 hover:bg-zinc-800">
              <Plus className="h-4 w-4 mr-1.5" /> Adicionar CEP
            </Button>
          </div>

          {enderecosList.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3 mb-1">
              {enderecosList.map(e => (
                <span key={e.cep} className="inline-flex items-center gap-1.5 bg-zinc-800 border border-zinc-700 rounded-full px-3 py-1 text-xs text-zinc-200">
                  🏢 {e.preview}
                  <button onClick={() => removerEnderecoAgente(e.cep)} disabled={running} className="text-zinc-500 hover:text-red-400">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className="mt-3">
            <label className="text-xs font-medium text-zinc-400 mb-1.5 block">Segmento <span className="text-zinc-600">(opcional — deixe vazio pra pegar qualquer tipo de negócio nesses endereços)</span></label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Select value={segmento || "__todos__"} onValueChange={v => { setSegmento(v === "__todos__" ? "" : v); setSegCustom(""); }} disabled={running}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-100"><SelectValue placeholder="Qualquer tipo de negócio..." /></SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="__todos__" className="text-zinc-100">🌐 Todos os segmentos</SelectItem>
                  {SEGMENTOS.map(s => <SelectItem key={s.value} value={s.value} className="text-zinc-100">{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input placeholder="Ou busca livre, ex: clínica odontológica..." value={segCustom} onChange={e => { setSegCustom(e.target.value); setSegmento(""); }}
                disabled={running} className="bg-zinc-800 border-zinc-700 text-zinc-100" />
            </div>
          </div>
          <p className="text-xs text-zinc-500 mt-2">
            💡 O Google Maps só mostra empresas já cadastradas nele — prédios com loja/clínica costumam aparecer bem, escritório "de fundo" às vezes não.
          </p>
        </div>
        )}

        <div className="mb-3">
          <label className="text-xs font-medium text-zinc-400 mb-1.5 block">
            {autopilotOn
              ? "Limite diário do piloto automático (máximo 50 leads/dia)"
              : "Quantos leads abordar nessa rodada (máximo 50/dia no total)"}
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Input type="number" min={1} max={50} value={maxLeads}
              onChange={e => setMaxLeads(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
              className="bg-zinc-800 border-zinc-700 text-zinc-100 w-28" disabled={running} />
            <span style={{ fontSize: 11.5, color: "#71717a" }}>
              O teto é 50/dia para proteger seu número de WhatsApp de banimento.
            </span>
          </div>
        </div>

        <div className="mb-3">
          <label className="text-xs font-medium text-zinc-400 mb-1.5 block">Mensagem de abertura</label>
          <p style={{ fontSize: 11.5, color: "#a1a1aa", margin: "0 0 8px 0", lineHeight: 1.5 }}>
            💬 Você pode colocar <strong style={{ color: "#f4f4f5" }}>{"{nome}"}</strong> pra mencionar o nome de cada lead, e <strong style={{ color: "#f4f4f5" }}>{"{endereco}"}</strong> pra mencionar o endereço dele. Os dois são opcionais — se o lead que você pegou não tiver nome ou endereço disponível, o placeholder some sozinho da frase, então monte a mensagem do jeito que achar melhor.
          </p>
          <textarea value={message} onChange={e => setMessage(e.target.value)} disabled={running} rows={3}
            className="w-full bg-zinc-800 border border-zinc-700 text-zinc-100 text-sm rounded-md p-2.5 resize-none" />
          <p style={{ fontSize: 11, color: "#71717a", margin: "5px 0 0 0" }}>
            💡 Dica: seja direto e não mencione que é um robô. Apresente-se com seu nome e empresa.
          </p>
        </div>

        <div className="mb-3" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid #3f3f46", borderRadius: 10, padding: "12px 12px" }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: isDark ? "#f4f4f5" : "#18181b", display: "block" }}>
            📶 Operadoras que você revende
          </span>
          <span style={{ fontSize: 11.5, color: "#a1a1aa", display: "block", marginTop: 2, marginBottom: 10 }}>
            O agente consulta a cobertura de todas as marcadas no CEP do lead — e se uma não atender
            mas outra sim, ele oferece a que atende. A Claro usa o book de ofertas embutido; pra Vivo
            e TIM, descreva suas ofertas no prompt de negociação ou anexe um book.
          </span>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
            {[
              { id: "claro", label: "Claro", cor: "#ef4444", logo: "/logo-claro.png", logoW: 46 },
              { id: "vivo",  label: "Vivo",  cor: "#a855f7", logo: "/logo-vivo.png",  logoW: 46 },
              { id: "tim",   label: "TIM",   cor: "#3b82f6", logo: "/logo-tim.png",   logoW: 52 },
            ].map((op) => {
              const ativo = operadoras.includes(op.id);
              return (
                <button
                  key={op.id}
                  type="button"
                  disabled={running}
                  onClick={() =>
                    setOperadoras((prev) =>
                      prev.includes(op.id) ? prev.filter((o) => o !== op.id) : [...prev, op.id]
                    )
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 14px",
                    borderRadius: 100,
                    cursor: running ? "default" : "pointer",
                    border: `1.5px solid ${ativo ? op.cor : "#3f3f46"}`,
                    background: ativo ? `${op.cor}22` : "transparent",
                    transition: "all .15s",
                  }}
                >
                  {ativo && <span style={{ fontSize: 12, color: op.cor, fontWeight: 700 }}>✓</span>}
                  <img
                    src={op.logo}
                    alt={op.label}
                    style={{
                      height: 16,
                      width: op.logoW,
                      objectFit: "contain",
                      filter: ativo ? "none" : "grayscale(1)",
                      opacity: ativo ? 1 : 0.45,
                      transition: "all .15s",
                    }}
                  />
                </button>
              );
            })}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11.5, color: "#a1a1aa" }}>Atendimento para:</span>
            {[
              { id: "pj", label: "🏢 Empresas (CNPJ)" },
              { id: "pf", label: "🏠 Pessoa física" },
            ].map((m) => (
              <label key={m.id} style={{ display: "flex", alignItems: "center", gap: 5, cursor: running ? "default" : "pointer", fontSize: 12.5, color: modoPessoa === m.id ? (isDark ? "#f4f4f5" : "#18181b") : "#a1a1aa", fontWeight: modoPessoa === m.id ? 600 : 400 }}>
                <input
                  type="radio"
                  name="modo-pessoa"
                  checked={modoPessoa === m.id}
                  onChange={() => setModoPessoa(m.id)}
                  disabled={running}
                  style={{ cursor: running ? "default" : "pointer" }}
                />
                {m.label}
              </label>
            ))}
            <span style={{ fontSize: 10.5, color: "#71717a", width: "100%" }}>
              O modo vale pra todas as operadoras marcadas — o agente ajusta o tom da conversa (residencial ou corporativo).
            </span>
          </div>
        </div>

        <div className="mb-3" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid #3f3f46", borderRadius: 10, padding: "10px 12px" }}>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: running ? "default" : "pointer" }}>
            <input
              type="checkbox"
              checked={useGif}
              onChange={e => setUseGif(e.target.checked)}
              disabled={running}
              style={{ marginTop: 2, width: 16, height: 16, cursor: running ? "default" : "pointer" }}
            />
            <span>
              <span style={{ fontSize: 13, fontWeight: 600, color: isDark ? "#f4f4f5" : "#18181b" }}>🎬 Enviar GIF junto com a mensagem de abertura</span>
              <span style={{ fontSize: 11.5, color: useGif ? "#f59e0b" : "#a1a1aa", display: "block", marginTop: 2 }}>
                {useGif
                  ? "⚠️ Ativado — o agente enviará o texto primeiro e a imagem de ofertas logo em seguida (duas mensagens separadas)."
                  : "Desativado — o agente envia apenas o texto puro (mais estável e confiável)."}
              </span>
            </span>
          </label>

          {useGif && (
            <div style={{ marginTop: 10, paddingLeft: 26 }}>
              <input type="file" ref={offerImageFileRef} accept=".jpg,.jpeg,.png,.webp" onChange={handleUploadOfferImage} style={{ display: "none" }} />
              {offerImageUrl ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <img src={offerImageUrl} alt="Imagem de ofertas" style={{ width: 44, height: 44, objectFit: "cover", borderRadius: 6, border: "1px solid #3f3f46" }} />
                  <button
                    type="button"
                    onClick={() => offerImageFileRef.current?.click()}
                    disabled={uploadingOfferImage}
                    style={{ fontSize: 11.5, color: "#a1a1aa", background: "none", border: "1px solid #3f3f46", borderRadius: 6, padding: "4px 10px", cursor: uploadingOfferImage ? "default" : "pointer" }}
                  >
                    {uploadingOfferImage ? "Enviando..." : "Trocar imagem"}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => offerImageFileRef.current?.click()}
                  disabled={uploadingOfferImage}
                  style={{ fontSize: 11.5, color: "#a1a1aa", background: "none", border: "1px dashed #3f3f46", borderRadius: 6, padding: "6px 12px", cursor: uploadingOfferImage ? "default" : "pointer" }}
                >
                  {uploadingOfferImage ? "Enviando..." : "📎 Anexar imagem de ofertas (JPG, PNG ou WEBP)"}
                </button>
              )}
              {!offerImageUrl && (
                <div style={{
                  marginTop: 8, padding: "8px 10px", borderRadius: 8,
                  background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.35)",
                  fontSize: 11.5, color: "#f59e0b", fontWeight: 500,
                }}>
                  ⚠️ Sem imagem anexada ainda — enquanto isso, o envio sai <u>só com o texto</u>, sem imagem nenhuma. O sistema nunca usa a imagem de outra conta como padrão.
                </div>
              )}
              <span style={{ fontSize: 10.5, color: "#71717a", display: "block", marginTop: 4 }}>
                {offerImageUrl
                  ? "Essa é a imagem que será enviada. Subir uma nova substitui a atual."
                  : "Anexe uma imagem pra ela ir junto com a mensagem de abertura."}
              </span>
            </div>
          )}
        </div>

        <div className="mb-3">
          <div className="flex items-center justify-between mb-1.5 flex-wrap gap-2">
            <label className="text-xs font-medium text-zinc-400 block">
              Prompt de negociação <span className="text-zinc-600">(como o agente conversa, negocia e fecha a venda){useClaroBook && <span className="text-zinc-600"> — opcional</span>}</span>
            </label>
            <input type="file" ref={bookFileRef} accept=".pdf,.docx" onChange={handleUploadBook} style={{ display: "none" }} />
            {offersBookFilename ? (
              <div className="flex items-center gap-1.5">
                <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600, color: "#4ade80", background: "rgba(74,222,128,0.1)", border: "1px solid rgba(74,222,128,0.3)", borderRadius: 20, padding: "3px 10px" }}>
                  📎 {offersBookFilename}
                </span>
                <button type="button" onClick={handleRemoveBook} title="Remover book" style={{ color: "#71717a", background: "none", border: "none", cursor: "pointer", padding: 2 }}>
                  <X size={13} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => bookFileRef.current?.click()}
                disabled={uploadingBook || running}
                title="Anexe o book de ofertas/produtos (PDF ou Word) em vez de escrever tudo no prompt — a IA lê e aprende sozinha"
                style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 600, color: uploadingBook ? "#52525b" : "#a1a1aa", background: "transparent", border: "1px solid #3f3f46", borderRadius: 20, padding: "4px 10px", cursor: uploadingBook ? "default" : "pointer" }}
              >
                {uploadingBook ? <Loader size={12} className="animate-spin" /> : "📎"}
                {uploadingBook ? "Processando..." : "Anexar book de ofertas (opcional)"}
              </button>
            )}
          </div>
          <textarea
            value={negotiationPrompt}
            onChange={e => setNegotiationPrompt(e.target.value)}
            disabled={running}
            rows={6}
            placeholder='Você pode colar aqui o prompt de como deseja que o agente converse — pode até pedir ajuda do ChatGPT ou do Claude pra escrever. Ex: "Você é a Camila, corretora da Imóveis XPTO. Quando o lead demonstrar interesse, pergunte orçamento e região, ofereça agendar uma visita, e nunca prometa valores sem antes consultar o estoque..."'
            className="w-full bg-zinc-800 border border-zinc-700 text-zinc-100 text-sm rounded-md p-2.5 resize-none placeholder:text-zinc-600 placeholder:italic"
          />
          <p style={{ fontSize: 11, color: "#71717a", margin: "5px 0 0 0" }}>
            {useClaroBook
              ? "✅ Opcional — você marcou que usa o book de ofertas Claro Empresas pronto. Se quiser, ainda pode escrever um prompt aqui pra ajustar o estilo de conversa."
              : offersBookFilename
              ? "✅ Book anexado! A IA já aprendeu as ofertas/produtos desse arquivo — o prompt acima (se preenchido) só define o estilo/persona por cima disso."
              : "⚠️ Obrigatório para ativar o piloto automático ou rodar o agente — é esse texto que define a personalidade, a oferta e a forma de negociar/fechar venda nesse segmento. Ou anexe um book de ofertas (📎 acima) em vez de escrever tudo."}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button onClick={handleRun} disabled={running} className="bg-purple-600 hover:bg-purple-500 px-8">
            {running ? <><Loader className="h-4 w-4 animate-spin mr-2" />Rodando agente...</> : <>🚀 Buscar e Abordar Agora (manual)</>}
          </Button>
          <Button onClick={handleSaveConfig} disabled={savingConfig || running} variant="outline" className="border-zinc-700 text-zinc-300">
            {savingConfig ? <><Loader className="h-4 w-4 animate-spin mr-2" />Salvando...</> : "Salvar configuração"}
          </Button>
        </div>
      </div>

      {run && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
          <div className="text-sm font-semibold text-zinc-100 mb-3">Resultado da rodada</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
            <div><div className="text-lg font-bold text-zinc-100">{run.found ?? "—"}</div><div className="text-[11px] text-zinc-500">Encontrados</div></div>
            <div><div className="text-lg font-bold text-zinc-400">{run.already_contacted ?? "—"}</div><div className="text-[11px] text-zinc-500">Já contatados antes</div></div>
            <div><div className="text-lg font-bold text-blue-400">{run.candidates ?? "—"}</div><div className="text-[11px] text-zinc-500">Selecionados</div></div>
            <div><div className="text-lg font-bold text-green-400">{run.sent ?? 0}</div><div className="text-[11px] text-zinc-500">Mensagens enviadas</div></div>
          </div>
          {run.failed > 0 && <div className="text-xs text-amber-400 mt-3">{run.failed} falharam ao enviar (sem WhatsApp ou erro).</div>}
          <div className="text-xs mt-3" style={{ color: running ? "#3b82f6" : run.status === "parado_pelo_usuario" ? "#f59e0b" : run.status === "parado_conexao_instavel" ? "#ef4444" : "#22c55e" }}>
            {running
              ? "Em andamento — enviando com pausa entre cada mensagem para não correr risco de bloqueio..."
              : run.status === "parado_pelo_usuario"
              ? "⏹️ Rodada parada por você — os leads restantes não foram contatados."
              : run.status === "parado_conexao_instavel"
              ? "🔌 Rodada interrompida: várias mensagens seguidas falharam, provável sinal de que a conexão do seu WhatsApp caiu no meio do envio. Verifique a conexão na aba WhatsApp e rode de novo."
              : "Rodada concluída ✅"}
          </div>
          {run.status === "parado_conexao_instavel" && (
            <button
              onClick={() => navigate("/whatsapp")}
              style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 7, width: "100%", marginTop: 10, padding: "9px 0", borderRadius: 8, background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.4)", color: "#ef4444", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
            >
              <MessageCircle size={14} /> Ver conexão do WhatsApp
            </button>
          )}
          {running && (
            <button
              onClick={handleStopRun}
              disabled={stopping}
              style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                width: "100%", marginTop: 12, padding: "9px 0", borderRadius: 8,
                background: "transparent", border: "1px solid rgba(239,68,68,0.4)",
                color: stopping ? "#71717a" : "#ef4444", fontSize: 13, fontWeight: 700,
                cursor: stopping ? "default" : "pointer",
              }}
            >
              {stopping ? <Loader size={14} className="animate-spin" /> : <XCircle size={14} />}
              {stopping ? "Parando..." : "Parar rodada"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Aba Mailing ──────────────────────────────────────────────
function MailingTab() {
  const [contacts, setContacts]     = useState([]);
  const [summary, setSummary]       = useState({ total: 0, pending: 0, sent: 0, failed: 0 });
  const [loading, setLoading]       = useState(true);
  const [running, setRunning]       = useState(false);
  const [importing, setImporting]   = useState(false);
  const [step, setStep]             = useState("list"); // "list" | "map"
  const [rawHeaders, setRawHeaders] = useState([]);
  const [rawRows, setRawRows]       = useState([]);
  const [colName, setColName]       = useState("");
  const [colPhone, setColPhone]     = useState("");
  const [colDDD, setColDDD]         = useState(""); // coluna de DDD separada (opcional — planilhas da Receita Federal)
  const [meiMailing, setMeiMailing] = useState(false); // este lote é de MEIs (autônomos), não empresas
  const [colCEP, setColCEP]         = useState(""); // coluna de CEP (opcional)
  const [colRua, setColRua]         = useState(""); // coluna de rua/logradouro (opcional)
  const [colBairro, setColBairro]   = useState(""); // coluna de bairro (opcional)
  const [colCidade, setColCidade]   = useState(""); // coluna de cidade (opcional)
  const [colEstado, setColEstado]   = useState(""); // coluna de estado/UF (opcional)
  const [colMEI, setColMEI]         = useState(""); // coluna de MEI por linha (opcional — vence o checkbox do lote)
  const [schedule, setSchedule]     = useState(null);   // status do disparo espaçado
  const [scheduleBusy, setScheduleBusy] = useState(false);
  const [showRunConfirm, setShowRunConfirm] = useState(false); // popup de aviso do disparo rápido
  const [runConfirmChecked, setRunConfirmChecked] = useState(false);
  const [notifyPhone, setNotifyPhone] = useState("");        // número pessoal p/ avisos de lead
  const [notifySaved, setNotifySaved] = useState("");        // último valor salvo no backend
  const [notifySaving, setNotifySaving] = useState(false);
  const fileRef = useRef(null);

  // Mesmo padrão do App.js: lê a classe "dark" do <html> pra saber o tema atual
  const [isDark, setIsDark] = useState(
    () => document.documentElement.classList.contains("dark")
  );
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  const fetchContacts = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/prospect/mailing");
      setContacts(data.contacts || []);
      setSummary({ total: data.total, pending: data.pending, sent: data.sent, failed: data.failed });
    } catch { if (!silent) toast.error("Erro ao carregar mailing"); }
    finally { if (!silent) setLoading(false); }
  }, []);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/prospect/notify-phone");
        setNotifyPhone(data.notify_phone || "");
        setNotifySaved(data.notify_phone || "");
      } catch { /* silencioso */ }
    })();
  }, []);

  const handleSaveNotifyPhone = async () => {
    setNotifySaving(true);
    try {
      const { data } = await api.post("/prospect/notify-phone", { notify_phone: notifyPhone });
      setNotifyPhone(data.notify_phone || "");
      setNotifySaved(data.notify_phone || "");
      toast.success(data.notify_phone
        ? "🔔 Avisos de lead ativados! Você vai receber uma mensagem nesse número na primeira interação de cada lead."
        : "Avisos de lead desativados.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar o número.");
    } finally { setNotifySaving(false); }
  };

  const fetchSchedule = useCallback(async () => {
    try {
      const { data } = await api.get("/prospect/mailing/schedule/status");
      setSchedule(data);
    } catch { /* silencioso — não trava a tela por causa do status */ }
  }, []);

  useEffect(() => {
    fetchSchedule();
    const interval = setInterval(() => {
      fetchSchedule();
      fetchContacts(true); // atualiza os contadores (Total/Pendentes/Enviados/Falharam) sem piscar a tela
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchSchedule, fetchContacts]);

  const handleScheduleStart = async () => {
    setScheduleBusy(true);
    try {
      const { data } = await api.post("/prospect/mailing/schedule/start", { max_leads: 50 });
      toast.success(`📨 Envio espaçado ligado! ${data.pending} contatos serão enviados aos poucos entre ${String(data.start_hour).padStart(2,"0")}h e ${String(data.end_hour).padStart(2,"0")}h.`);
      fetchSchedule();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao ligar o envio espaçado.");
    } finally { setScheduleBusy(false); }
  };

  const handleScheduleStop = async () => {
    setScheduleBusy(true);
    try {
      await api.post("/prospect/mailing/schedule/stop");
      toast.success("Envio espaçado pausado.");
      fetchSchedule();
    } catch { toast.error("Erro ao pausar o envio espaçado."); }
    finally { setScheduleBusy(false); }
  };

  // Parser de CSV que respeita aspas — essencial pra campos como
  // "R$ 100,00" (capital social) ou endereços/listas com vírgula dentro
  // de aspas. Um split ingênuo por vírgula quebra esses campos em dois,
  // deslocando TODAS as colunas seguintes pra direita naquela linha —
  // foi exatamente o bug que fazia o telefone importado vir errado
  // (pegava o valor da coluna vizinha, tipo natureza_juridica).
  const parseCsvText = (text) => {
    const delimiter = (text.slice(0, 2000).match(/;/g) || []).length >
                       (text.slice(0, 2000).match(/,/g) || []).length ? ";" : ",";
    const rows = [];
    let row = [], field = "", inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (inQuotes) {
        if (c === '"') {
          if (text[i + 1] === '"') { field += '"'; i++; }
          else inQuotes = false;
        } else field += c;
      } else if (c === '"') {
        inQuotes = true;
      } else if (c === delimiter) {
        row.push(field); field = "";
      } else if (c === "\n" || c === "\r") {
        if (c === "\r" && text[i + 1] === "\n") i++;
        row.push(field); field = "";
        if (row.length > 1 || row[0] !== "") rows.push(row);
        row = [];
      } else field += c;
    }
    if (field !== "" || row.length) { row.push(field); rows.push(row); }
    return rows;
  };

  // Lê o CSV/XLSX no navegador, sem enviar o arquivo pro servidor
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    const ext = file.name.split(".").pop().toLowerCase();
    let rows = [];

    if (ext === "csv") {
      const text = await file.text();
      rows = parseCsvText(text);
    } else if (ext === "xlsx" || ext === "xls") {
      try {
        const XLSX = await import("xlsx");
        const ab = await file.arrayBuffer();
        const wb = XLSX.read(ab, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
      } catch {
        toast.error("Erro ao ler planilha. Tente salvar como CSV e importar novamente.");
        return;
      }
    } else {
      toast.error("Formato não suportado. Use CSV, XLS ou XLSX.");
      return;
    }

    if (rows.length < 2) { toast.error("Planilha vazia ou sem dados."); return; }

    const headers = rows[0].map(h => String(h).trim());
    setRawHeaders(headers);
    setRawRows(rows.slice(1));

    // Tenta adivinhar as colunas automaticamente
    const guessName = headers.findIndex(h => /nome|name|cliente|contact|social|fantasia/i.test(h));

    // Amostra algumas linhas pra validar pelo conteúdo, não só pelo nome da coluna
    const sample = rows.slice(1, 21);
    const looksLikePhone = (val) => {
      const digits = String(val ?? "").replace(/\D/g, "");
      return digits.length >= 8 && digits.length <= 13;
    };
    const scoreColumn = (i) => sample.filter(r => looksLikePhone(r[i])).length;

    // 1) Prioriza cabeçalhos claramente de telefone/whatsapp (nunca "numero" sozinho,
    //    que costuma ser número de endereço em planilhas de leads/Receita Federal)
    const strongPhoneRegex = /tel|fone|celular|whatsapp|phone|contato/i;
    let guessPhone = headers.findIndex(h => strongPhoneRegex.test(h));

    // 2) Se não achou por nome, procura colunas com "numero"/"número" e confirma pelo
    //    conteúdo (precisa parecer telefone em boa parte da amostra)
    if (guessPhone < 0) {
      const candidates = headers
        .map((h, i) => ({ i, hits: /numero|número/i.test(h) ? scoreColumn(i) : -1 }))
        .filter(c => c.hits > 0)
        .sort((a, b) => b.hits - a.hits);
      guessPhone = candidates.length ? candidates[0].i : -1;
    }

    // 3) Último recurso: varre todas as colunas e usa a que mais parece telefone
    if (guessPhone < 0 && sample.length) {
      const scored = headers
        .map((_, i) => ({ i, hits: scoreColumn(i) }))
        .sort((a, b) => b.hits - a.hits);
      if (scored.length && scored[0].hits >= Math.ceil(sample.length * 0.5)) {
        guessPhone = scored[0].i;
      }
    }

    // Tenta achar coluna de DDD separada (comum em bases da Receita Federal:
    // ex. "ddd_1" + "telefone_1", onde telefone_1 não tem o DDD embutido).
    // Só considera DDD se a coluna de telefone escolhida tiver poucos dígitos
    // (8 ou 9 — número local, sem DDD).
    let guessDDD = -1;
    if (guessPhone >= 0) {
      const phoneDigitsLen = sample
        .map(r => String(r[guessPhone] ?? "").replace(/\D/g, "").length)
        .filter(n => n > 0);
      const mostlyLocalNumber = phoneDigitsLen.length > 0 &&
        phoneDigitsLen.filter(n => n === 8 || n === 9).length >= Math.ceil(phoneDigitsLen.length * 0.6);
      if (mostlyLocalNumber) {
        const dddCandidates = headers
          .map((h, i) => ({ i, hits: /ddd/i.test(h) ? sample.filter(r => /^\d{2,3}$/.test(String(r[i] ?? "").trim())).length : -1 }))
          .filter(c => c.hits > 0)
          .sort((a, b) => b.hits - a.hits);
        guessDDD = dddCandidates.length ? dddCandidates[0].i : -1;
      }
    }

    setColName(guessName  >= 0 ? String(guessName)  : "");
    setColPhone(guessPhone >= 0 ? String(guessPhone) : "");
    setColDDD(guessDDD >= 0 ? String(guessDDD) : "");

    // Auto-detecção das colunas opcionais de endereço/MEI — todas opcionais,
    // se não achar nenhuma o import segue normal (a abordagem de "confirmar
    // endereço" só existe quando esses dados estão presentes).
    const guessCEP    = headers.findIndex(h => /^cep$/i.test(h.trim()) || /\bcep\b/i.test(h));
    // "Rua" prioriza nome de coluna exato ("RUA", "LOGRADOURO") antes de
    // cair no padrão genérico "endereco" — senão casa errado com colunas
    // tipo "MESMO_ENDERECO" que só indicam SIM/NÃO, não o nome da rua.
    const guessRua = (() => {
      const forte = headers.findIndex(h => /^rua$|^logradouro$/i.test(h.trim()));
      if (forte >= 0) return forte;
      return headers.findIndex(h => /rua|logradouro|endereco|endereço/i.test(h));
    })();
    const guessBairro  = headers.findIndex(h => /bairro/i.test(h));
    const guessCidade  = headers.findIndex(h => /cidade|munic[ií]pio|localidade/i.test(h));
    const guessEstado  = headers.findIndex(h => /^uf$/i.test(h.trim()) || /estado/i.test(h));
    const guessMEI     = headers.findIndex(h => /^mei$/i.test(h.trim()));

    setColCEP(guessCEP >= 0 ? String(guessCEP) : "");
    setColRua(guessRua >= 0 ? String(guessRua) : "");
    setColBairro(guessBairro >= 0 ? String(guessBairro) : "");
    setColCidade(guessCidade >= 0 ? String(guessCidade) : "");
    setColEstado(guessEstado >= 0 ? String(guessEstado) : "");
    setColMEI(guessMEI >= 0 ? String(guessMEI) : "");

    setStep("map");
  };

  const handleImport = async () => {
    if (colPhone === "") { toast.error("Selecione a coluna de número de WhatsApp."); return; }
    setImporting(true);
    try {
      const phoneIdx = parseInt(colPhone);
      const nameIdx  = colName !== "" ? parseInt(colName) : -1;
      const dddIdx   = colDDD !== "" ? parseInt(colDDD) : -1;
      const cepIdx    = colCEP !== "" ? parseInt(colCEP) : -1;
      const ruaIdx    = colRua !== "" ? parseInt(colRua) : -1;
      const bairroIdx = colBairro !== "" ? parseInt(colBairro) : -1;
      const cidadeIdx = colCidade !== "" ? parseInt(colCidade) : -1;
      const estadoIdx = colEstado !== "" ? parseInt(colEstado) : -1;
      const meiIdx    = colMEI !== "" ? parseInt(colMEI) : -1;
      const contacts = rawRows
        .map(row => {
          const rawPhone = String(row[phoneIdx] || "").trim();
          const ddd = dddIdx >= 0 ? String(row[dddIdx] || "").replace(/\D/g, "") : "";
          // Se tem coluna de DDD separada, concatena na frente do número local
          // (a normalização do 9º dígito e do DDI é feita no backend).
          const phone = ddd ? `${ddd}${rawPhone.replace(/\D/g, "")}` : rawPhone;
          return {
            phone,
            name: nameIdx >= 0 ? String(row[nameIdx] || "").trim() : "Contato",
            cep: cepIdx >= 0 ? String(row[cepIdx] || "").trim() : "",
            rua: ruaIdx >= 0 ? String(row[ruaIdx] || "").trim() : "",
            bairro: bairroIdx >= 0 ? String(row[bairroIdx] || "").trim() : "",
            cidade: cidadeIdx >= 0 ? String(row[cidadeIdx] || "").trim() : "",
            estado: estadoIdx >= 0 ? String(row[estadoIdx] || "").trim() : "",
            mei: meiIdx >= 0 ? String(row[meiIdx] || "").trim() : "",
          };
        })
        .filter(c => c.phone.length >= 8);

      if (contacts.length === 0) { toast.error("Nenhum número válido encontrado."); return; }

      const { data } = await api.post("/prospect/mailing/import", { contacts, mei_mailing: meiMailing });
      toast.success(`✅ ${data.imported} contatos importados! (${data.skipped} ignorados — duplicatas ou inválidos)`);
      setStep("list");
      setMeiMailing(false);
      setColCEP(""); setColRua(""); setColBairro(""); setColCidade(""); setColEstado(""); setColMEI("");
      fetchContacts();
    } catch (e) {
      toast.error("Erro ao importar contatos.");
    } finally { setImporting(false); }
  };

  const handleRun = async () => {
    setShowRunConfirm(false);
    setRunConfirmChecked(false);
    setRunning(true);
    try {
      const { data } = await api.post("/prospect/mailing/run", { max_leads: 50 });
      if (data.dispatched === 0) {
        toast.info("Nenhum contato pendente no mailing.");
      } else {
        toast.success(`🚀 Disparando mensagens para ${data.dispatched} contatos! Restantes pendentes: ${data.remaining_pending}`);
      }
      setTimeout(fetchContacts, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao rodar mailing.");
    } finally { setRunning(false); }
  };

  const handleClear = async () => {
    if (!window.confirm("Tem certeza? Isso vai apagar TODOS os contatos do mailing.")) return;
    try {
      await api.delete("/prospect/mailing");
      toast.success("Mailing limpo.");
      fetchContacts();
    } catch { toast.error("Erro ao limpar mailing."); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/prospect/mailing/${id}`);
      setContacts(cs => cs.filter(c => c.id !== id));
      setSummary(s => ({ ...s, total: s.total - 1, pending: s.pending - 1 }));
    } catch { toast.error("Erro ao remover contato."); }
  };

  const cardStyle = { background: isDark ? "#18181b" : "rgb(240, 240, 255)", border: "1px solid #27272a", borderRadius: 12, padding: "12px 16px" };
  const selectStyle = { width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #3f3f46", background: "#09090b", color: "#f4f4f5", fontSize: 13 };

  // ── Etapa de mapeamento de colunas ─────────────────────────
  if (step === "map") {
    return (
      <div style={{ padding: "20px 0" }}>
        <button onClick={() => setStep("list")} style={{ background: "none", border: "none", color: "#a1a1aa", cursor: "pointer", fontSize: 13, marginBottom: 16, display: "flex", alignItems: "center", gap: 6 }}>
          <ArrowLeft size={14} /> Voltar
        </button>
        <div style={cardStyle}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: isDark ? "#f4f4f5" : "#18181b", marginBottom: 4 }}>📋 Mapeamento de colunas</h3>
          <p style={{ fontSize: 12.5, color: "#a1a1aa", marginBottom: 16 }}>
            Encontramos <strong style={{ color: isDark ? "#f4f4f5" : "#18181b" }}>{rawRows.length} contatos</strong> com {rawHeaders.length} colunas. Diga qual coluna é o número e qual é o nome:
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: "#a1a1aa", display: "block", marginBottom: 6 }}>📞 Coluna de WhatsApp/Telefone <span style={{ color: "#ef4444" }}>*</span></label>
              <select value={colPhone} onChange={e => setColPhone(e.target.value)} style={selectStyle}>
                <option value="" style={{ color: "#18181b" }}>Selecione...</option>
                {rawHeaders.map((h, i) => <option key={i} value={i} style={{ color: "#18181b" }}>{h || `Coluna ${i + 1}`}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: "#a1a1aa", display: "block", marginBottom: 6 }}>👤 Coluna de Nome <span style={{ color: "#71717a" }}>(opcional)</span></label>
              <select value={colName} onChange={e => setColName(e.target.value)} style={selectStyle}>
                <option value="" style={{ color: "#18181b" }}>Sem nome</option>
                {rawHeaders.map((h, i) => <option key={i} value={i} style={{ color: "#18181b" }}>{h || `Coluna ${i + 1}`}</option>)}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: "#a1a1aa", display: "block", marginBottom: 6 }}>
              🔢 Coluna de DDD separada <span style={{ color: "#71717a" }}>(opcional — use se o telefone não tem DDD junto, comum em bases da Receita Federal)</span>
            </label>
            <select value={colDDD} onChange={e => setColDDD(e.target.value)} style={selectStyle}>
              <option value="" style={{ color: "#18181b" }}>Sem coluna de DDD (telefone já vem completo)</option>
              {rawHeaders.map((h, i) => <option key={i} value={i} style={{ color: "#18181b" }}>{h || `Coluna ${i + 1}`}</option>)}
            </select>
          </div>

          {/* Preview das primeiras 3 linhas */}
          {rawRows.slice(0, 3).length > 0 && (
            <div style={{ background: "#09090b", borderRadius: 8, padding: "10px 12px", marginBottom: 16 }}>
              <p style={{ fontSize: 11, color: "#71717a", marginBottom: 6 }}>Prévia (3 primeiros):</p>
              {rawRows.slice(0, 3).map((row, i) => {
                const rawPhone = colPhone !== "" ? String(row[parseInt(colPhone)] || "") : "";
                const ddd = colDDD !== "" ? String(row[parseInt(colDDD)] || "").replace(/\D/g, "") : "";
                const preview = colPhone === "" ? "?" : (ddd ? `${ddd}${rawPhone.replace(/\D/g, "")}` : rawPhone);
                return (
                  <div key={i} style={{ fontSize: 12, color: "#a1a1aa", marginBottom: 3 }}>
                    <span style={{ color: "#22c55e" }}>{preview || "—"}</span>
                    {colName !== "" && <span style={{ color: "#94a3b8" }}> — {row[parseInt(colName)] || "—"}</span>}
                  </div>
                );
              })}
            </div>
          )}

          {/* Colunas opcionais de endereço/MEI — se o CSV tiver essas colunas
              (auto-detectadas acima), o bot pode confirmar o endereço antes
              de checar cobertura, sem esperar o lead digitar CEP. Totalmente
              opcional: sem essas colunas, o import funciona normal. */}
          {(colCEP !== "" || colRua !== "" || colCidade !== "" || colEstado !== "" || colMEI !== "") && (
            <div style={{ background: "rgba(59,130,246,0.06)", border: "1px solid #3b82f680", borderRadius: 10, padding: "10px 12px", marginBottom: 16 }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: isDark ? "#f4f4f5" : "#18181b", marginBottom: 4 }}>
                📍 Detectamos colunas de endereço/MEI nessa planilha
              </p>
              <p style={{ fontSize: 11.5, color: "#a1a1aa", marginBottom: 10 }}>
                Confirme se o mapeamento está certo. Com endereço, o bot pode perguntar "ainda é esse
                seu endereço?" e checar cobertura antes mesmo do lead digitar o CEP. A coluna de MEI
                (se existir) vale mais que o checkbox abaixo, contato por contato.
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                {[
                  ["CEP", colCEP, setColCEP],
                  ["Rua/Logradouro", colRua, setColRua],
                  ["Bairro", colBairro, setColBairro],
                  ["Cidade", colCidade, setColCidade],
                  ["Estado/UF", colEstado, setColEstado],
                  ["MEI (SIM/NÃO)", colMEI, setColMEI],
                ].map(([label, val, setter]) => (
                  <div key={label}>
                    <label style={{ fontSize: 10.5, color: "#a1a1aa", display: "block", marginBottom: 3 }}>{label}</label>
                    <select value={val} onChange={e => setter(e.target.value)} style={{ ...selectStyle, fontSize: 11.5, padding: "5px 6px" }}>
                      <option value="" style={{ color: "#18181b" }}>— nenhuma —</option>
                      {rawHeaders.map((h, i) => <option key={i} value={i} style={{ color: "#18181b" }}>{h || `Coluna ${i + 1}`}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Checkbox MEI — só afeta a conversa DEPOIS que o lead responder
              (o bot não vai mais chamar de "administrador da empresa").
              Não tem efeito nenhum em leads do Google Places. */}
          <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid #3f3f46", borderRadius: 10, padding: "10px 12px", marginBottom: 16 }}>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={meiMailing}
                onChange={e => setMeiMailing(e.target.checked)}
                style={{ marginTop: 2, width: 16, height: 16, cursor: "pointer" }}
              />
              <span>
                <span style={{ fontSize: 13, fontWeight: 600, color: isDark ? "#f4f4f5" : "#18181b" }}>
                  👤 Este mailing é de MEIs (autônomos)
                </span>
                <span style={{ fontSize: 11.5, color: "#a1a1aa", display: "block", marginTop: 2 }}>
                  Marque se esses contatos são profissionais autônomos com CNPJ (não empresas com
                  hierarquia). Depois que o lead responder, o bot vai falar direto com a pessoa —
                  sem perguntar se ela é "a administradora" ou "trabalha na administração". Não
                  afeta leads vindos do Google Places (esses continuam sendo negócios de verdade).
                </span>
              </span>
            </label>
            {meiMailing && (
              <div style={{ marginTop: 10, background: "#09090b", borderRadius: 8, padding: "10px 12px" }}>
                <p style={{ fontSize: 11, color: "#71717a", marginBottom: 4 }}>
                  💡 Sua primeira mensagem é enviada do jeito que você escreveu — o checkbox não
                  reescreve ela. Se seu template atual pergunta algo tipo "você é o administrador da
                  empresa?", considere trocar por algo assim pra esse lote:
                </p>
                <p style={{ fontSize: 12, color: "#a1a1aa", fontStyle: "italic" }}>
                  "Olá, boa tarde! Esse número é do(a) {"{nome}"}? Sou o [seu nome], da [operadora].
                  Tenho uma promoção de internet fibra que pode te interessar."
                </p>
              </div>
            )}
          </div>

          <button
            onClick={handleImport}
            disabled={importing || colPhone === ""}
            style={{ width: "100%", padding: "10px 0", borderRadius: 8, background: importing || colPhone === "" ? "#27272a" : "#3b82f6", color: "#fff", fontWeight: 700, fontSize: 14, border: "none", cursor: importing || colPhone === "" ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
          >
            {importing ? <Loader size={15} className="animate-spin" /> : <Upload size={15} />}
            {importing ? "Importando..." : `Importar ${rawRows.length} contatos`}
          </button>
        </div>
      </div>
    );
  }

  // ── Listagem principal ─────────────────────────────────────
  return (
    <div style={{ padding: "20px 0" }}>
      {/* Cards de resumo */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 20 }}>
        {[
          { label: "Total", value: summary.total, color: isDark ? "#f4f4f5" : "#52525b" },
          { label: "Pendentes", value: summary.pending, color: "#f59e0b" },
          { label: "Enviados", value: summary.sent, color: "#22c55e" },
          { label: "Falharam", value: summary.failed, color: "#ef4444" },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ ...cardStyle, textAlign: "center" }}>
            <div style={{ fontSize: 18, fontWeight: 800, color }}>{value}</div>
            <div style={{ fontSize: 11, color: "#71717a", marginTop: 2 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Envio espaçado (recomendado) */}
      <div style={{ ...cardStyle, marginBottom: 16, textAlign: "left" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: isDark ? "#f4f4f5" : "#18181b", display: "flex", alignItems: "center", gap: 6 }}>
              📨 Envio espaçado <span style={{ fontSize: 10, fontWeight: 700, color: "#22c55e", background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.3)", borderRadius: 20, padding: "1px 8px" }}>recomendado</span>
            </div>
            <div style={{ fontSize: 12, color: "#71717a", marginTop: 3 }}>
              Manda 1 contato por vez, em intervalos aleatórios de 8 a 15 minutos, só entre {schedule ? String(schedule.start_hour).padStart(2,"0") : "08"}h e {schedule ? String(schedule.end_hour).padStart(2,"0") : "18"}h — reduz o risco de ban por padrão de disparo em massa.
            </div>
            {schedule?.enabled && (
              <div style={{ fontSize: 12, color: "#22c55e", marginTop: 6, fontWeight: 600 }}>
                🟢 Ativo — {schedule.sent_today}/{schedule.daily_limit} enviados hoje
                {schedule.next_send_at && ` · próximo envio ${new Date(schedule.next_send_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`}
              </div>
            )}
          </div>
          <button
            onClick={schedule?.enabled ? handleScheduleStop : handleScheduleStart}
            disabled={scheduleBusy || (!schedule?.enabled && summary.pending === 0)}
            style={{
              display: "flex", alignItems: "center", gap: 7, padding: "9px 18px", borderRadius: 8, border: "none",
              background: scheduleBusy || (!schedule?.enabled && summary.pending === 0) ? "#27272a" : (schedule?.enabled ? "#ef4444" : "#22c55e"),
              color: scheduleBusy || (!schedule?.enabled && summary.pending === 0) ? "#71717a" : (schedule?.enabled ? "#fff" : "#000"),
              fontSize: 13, fontWeight: 700,
              cursor: scheduleBusy || (!schedule?.enabled && summary.pending === 0) ? "default" : "pointer",
              flexShrink: 0,
            }}
          >
            {scheduleBusy ? <Loader size={15} className="animate-spin" /> : <Play size={15} />}
            {schedule?.enabled ? "Pausar envio espaçado" : "Ligar envio espaçado"}
          </button>
        </div>
      </div>

      {/* Aviso de lead no número pessoal (opcional) */}
      <div style={{ ...cardStyle, marginBottom: 16, textAlign: "left" }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: isDark ? "#f4f4f5" : "#18181b", display: "flex", alignItems: "center", gap: 6 }}>
          🔔 Aviso de lead no seu número pessoal <span style={{ fontSize: 10, fontWeight: 700, color: "#3b82f6", background: "rgba(59,130,246,0.12)", border: "1px solid rgba(59,130,246,0.3)", borderRadius: 20, padding: "1px 8px" }}>opcional</span>
        </div>
        <div style={{ fontSize: 12, color: "#71717a", marginTop: 3, marginBottom: 10 }}>
          Usando um número exclusivo pro disparo? Cadastre aqui o seu número real e, quando um lead interagir, você recebe na hora um aviso com o nome e o contato dele — direto no seu WhatsApp pessoal.
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input
            type="tel"
            value={notifyPhone}
            onChange={e => setNotifyPhone(e.target.value)}
            placeholder="Ex: 11981429962 (DDD + número)"
            style={{ flex: 1, minWidth: 220, padding: "9px 12px", borderRadius: 8, border: "1px solid #3f3f46", background: isDark ? "#09090b" : "#fff", color: isDark ? "#f4f4f5" : "#18181b", fontSize: 13 }}
          />
          <button
            onClick={handleSaveNotifyPhone}
            disabled={notifySaving || notifyPhone.trim() === notifySaved}
            style={{
              padding: "9px 18px", borderRadius: 8, border: "none",
              background: notifySaving || notifyPhone.trim() === notifySaved ? "#27272a" : "#3b82f6",
              color: notifySaving || notifyPhone.trim() === notifySaved ? "#71717a" : "#fff",
              fontSize: 13, fontWeight: 700,
              cursor: notifySaving || notifyPhone.trim() === notifySaved ? "default" : "pointer",
              flexShrink: 0,
            }}
          >
            {notifySaving ? "Salvando..." : (notifySaved && !notifyPhone.trim() ? "Desativar avisos" : "Salvar")}
          </button>
        </div>
        {notifySaved && (
          <div style={{ fontSize: 12, color: "#22c55e", marginTop: 8, fontWeight: 600 }}>
            🟢 Avisos ativos pro número {notifySaved}
          </div>
        )}
      </div>

      {/* Ações */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
        <input type="file" ref={fileRef} accept=".csv,.xlsx,.xls" onChange={handleFile} style={{ display: "none" }} />
        <button
          onClick={() => fileRef.current?.click()}
          style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 16px", borderRadius: 8, background: "#27272a", border: "1px solid #3f3f46", color: "#f4f4f5", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
        >
          <FileSpreadsheet size={15} /> Importar CSV / Excel
        </button>
        <button
          onClick={() => { setRunConfirmChecked(false); setShowRunConfirm(true); }}
          disabled={running || summary.pending === 0 || schedule?.enabled}
          title="Modo rápido: manda tudo em minutos, com pausa curta entre cada um. Use só pra lotes pequenos ou testes — pra listas grandes prefira o envio espaçado acima."
          style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 16px", borderRadius: 8, background: "transparent", border: "1px solid #3f3f46", color: running || summary.pending === 0 || schedule?.enabled ? "#52525b" : "#a1a1aa", fontSize: 12.5, fontWeight: 600, cursor: running || summary.pending === 0 || schedule?.enabled ? "default" : "pointer" }}
        >
          {running ? <Loader size={14} className="animate-spin" /> : <Play size={14} />}
          {running ? "Disparando..." : "Disparo rápido (avançado)"}
        </button>
        {summary.total > 0 && (
          <button
            onClick={handleClear}
            style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 16px", borderRadius: 8, background: "transparent", border: "1px solid #3f3f46", color: "#ef4444", fontSize: 13, fontWeight: 600, cursor: "pointer", marginLeft: "auto" }}
          >
            <Trash2 size={14} /> Limpar tudo
          </button>
        )}
      </div>

      {/* Popup de aviso do disparo rápido */}
      {showRunConfirm && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
          onClick={() => setShowRunConfirm(false)}
        >
          <div
            style={{ background: isDark ? "#18181b" : "#fff", border: "1px solid #3f3f46", borderRadius: 14, padding: "24px", maxWidth: 440, width: "100%", position: "relative" }}
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => setShowRunConfirm(false)}
              aria-label="Fechar"
              style={{ position: "absolute", top: 12, right: 12, background: "none", border: "none", color: "#71717a", cursor: "pointer", padding: 4, lineHeight: 0 }}
            >
              <X size={18} />
            </button>
            <div style={{ fontSize: 34, marginBottom: 10 }}>⚠️</div>
            <h3 style={{ fontSize: 16, fontWeight: 800, color: isDark ? "#f4f4f5" : "#18181b", marginBottom: 8 }}>
              Atenção: risco de ban
            </h3>
            <p style={{ fontSize: 13.5, color: isDark ? "#a1a1aa" : "#52525b", marginBottom: 16, lineHeight: 1.5 }}>
              Está ciente que, se for por esse método, terá um risco de receber um ban? O disparo rápido manda todas as mensagens em sequência, com pausa curta entre elas — o WhatsApp pode detectar como disparo em massa. Pra listas grandes, o <strong>envio espaçado</strong> é bem mais seguro.
            </p>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: isDark ? "#d4d4d8" : "#3f3f46", marginBottom: 18, cursor: "pointer", userSelect: "none" }}>
              <input
                type="checkbox"
                checked={runConfirmChecked}
                onChange={e => setRunConfirmChecked(e.target.checked)}
                style={{ marginTop: 2, cursor: "pointer" }}
              />
              Entendo os riscos e quero seguir com o disparo rápido mesmo assim.
            </label>
            <button
              onClick={handleRun}
              disabled={!runConfirmChecked}
              style={{
                width: "100%", padding: "11px 0", borderRadius: 9, border: "none",
                background: runConfirmChecked ? "#f59e0b" : (isDark ? "#27272a" : "#e4e4e7"),
                color: runConfirmChecked ? "#000" : "#71717a",
                fontSize: 14, fontWeight: 800,
                cursor: runConfirmChecked ? "pointer" : "default",
              }}
            >
              Estou ciente
            </button>
          </div>
        </div>
      )}

      {/* Lista de contatos */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Loader size={24} className="animate-spin" style={{ margin: "0 auto" }} color="#71717a" />
        </div>
      ) : contacts.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: "center", padding: "48px 24px" }}>
          <Users size={40} style={{ color: "#3f3f46", margin: "0 auto 12px", display: "block" }} />
          <p style={{ fontWeight: 700, color: isDark ? "#f4f4f5" : "#18181b", marginBottom: 6 }}>Nenhum contato no mailing ainda</p>
          <p style={{ fontSize: 13, color: "#71717a", marginBottom: 16 }}>Importe uma planilha CSV ou Excel com seus contatos para começar.</p>
          <button
            onClick={() => fileRef.current?.click()}
            style={{ padding: "9px 20px", borderRadius: 8, background: "#3b82f6", border: "none", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}
          >
            <Upload size={14} /> Importar agora
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {contacts.slice(0, 200).map(c => (
            <div key={c.id} style={{ ...cardStyle, display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: isDark ? "#f4f4f5" : "#18181b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {c.name}
                </div>
                <div style={{ fontSize: 12, color: "#71717a" }}>{c.phone}</div>
              </div>
              <span style={{
                fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 20,
                background: c.status === "sent" ? "rgba(34,197,94,0.1)" : c.status === "failed" ? "rgba(239,68,68,0.1)" : "rgba(245,158,11,0.1)",
                color: c.status === "sent" ? "#22c55e" : c.status === "failed" ? "#ef4444" : "#f59e0b",
              }}>
                {c.status === "sent" ? "Enviado" : c.status === "failed" ? "Falhou" : "Pendente"}
              </span>
              {c.status === "pending" && (
                <button onClick={() => handleDelete(c.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#71717a", padding: 4, flexShrink: 0 }}>
                  <X size={14} />
                </button>
              )}
            </div>
          ))}
          {contacts.length > 200 && (
            <p style={{ textAlign: "center", fontSize: 12, color: "#71717a", padding: 10 }}>
              Mostrando 200 de {contacts.length} contatos.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Aba Busca ────────────────────────────────────────────────
function BuscaTab({ onAddToKanban, onOpenChat }) {
  const [cidade,        setCidade]        = useState("");
  const [bairro,        setBairro]        = useState("");
  const [segmento,      setSegmento]      = useState("");
  const [segCustom,     setSegCustom]     = useState("");
  const [whatsappOnly,  setWhatsappOnly]  = useState(false);
  const [results,       setResults]       = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [searched,      setSearched]      = useState(false);

  const [cidadeSugs,    setCidadeSugs]    = useState([]);
  const [cidadeLoading, setCidadeLoading] = useState(false);
  const cidadeTimer = useRef(null);

  const [bairroSugs,    setBairroSugs]    = useState([]);
  const [bairroLoading, setBairroLoading] = useState(false);
  const bairroTimer = useRef(null);

  const [searchQuota, setSearchQuota] = useState(null); // { used_today, remaining_today, daily_limit }

  // ── Modo "buscar por endereço" (CEP + número) ────────────────────
  const [modoBusca, setModoBusca] = useState("segmento"); // "segmento" | "endereco"
  const [cepInput, setCepInput] = useState("");
  const [numeroInput, setNumeroInput] = useState("");
  const [enderecosList, setEnderecosList] = useState([]); // [{cep, numero, preview}]
  const [cepPreview, setCepPreview] = useState(null); // {logradouro, bairro, cidade, uf} do CEP sendo digitado
  const [cepPreviewLoading, setCepPreviewLoading] = useState(false);
  const cepPreviewTimer = useRef(null);
  const cepPreviewHideTimer = useRef(null);

  useEffect(() => {
    clearTimeout(cepPreviewTimer.current);
    clearTimeout(cepPreviewHideTimer.current);
    const cepLimpo = cepInput.replace(/\D/g, "");
    if (cepLimpo.length !== 8) { setCepPreview(null); return; }
    cepPreviewTimer.current = setTimeout(async () => {
      setCepPreviewLoading(true);
      try {
        const { data } = await api.get("/cep/lookup", { params: { cep: cepLimpo } });
        setCepPreview(data);
        // Some sozinho depois de 10s parado (sem digitar CEP novo)
        cepPreviewHideTimer.current = setTimeout(() => setCepPreview(null), 10000);
      } catch {
        setCepPreview({ erro: true });
        cepPreviewHideTimer.current = setTimeout(() => setCepPreview(null), 10000);
      } finally { setCepPreviewLoading(false); }
    }, 400);
    return () => clearTimeout(cepPreviewTimer.current);
  }, [cepInput]);

  const adicionarEndereco = () => {
    const cepLimpo = cepInput.replace(/\D/g, "");
    if (cepLimpo.length !== 8) { toast.error("CEP inválido — precisa ter 8 dígitos."); return; }
    if (enderecosList.some(e => e.cep === cepLimpo)) { toast.error("Esse CEP já foi adicionado."); return; }
    setEnderecosList(prev => [...prev, {
      cep: cepLimpo, numero: numeroInput.trim(),
      preview: cepPreview && !cepPreview.erro ? `${cepPreview.logradouro}${numeroInput.trim() ? ", " + numeroInput.trim() : ""} — ${cepPreview.bairro}` : cepLimpo,
    }]);
    setCepInput(""); setNumeroInput(""); setCepPreview(null);
  };

  const removerEndereco = (cep) => setEnderecosList(prev => prev.filter(e => e.cep !== cep));

  const buscarPorEndereco = useCallback(async () => {
    if (enderecosList.length === 0) { toast.error("Adicione pelo menos um CEP."); return; }
    setLoading(true); setResults([]); setSearched(true);
    try {
      const { data } = await api.post("/prospect/search-by-address", {
        enderecos: enderecosList.map(e => ({ cep: e.cep, numero: e.numero })),
        segmento: (segCustom.trim() || segmento || ""),
      });
      const items = data.results || [];
      setResults(items);
      setSearchQuota({ used_today: data.used_today, remaining_today: data.remaining_today, daily_limit: data.daily_limit });
      if (data.erros?.length) data.erros.forEach(erro => toast.error(erro));
      if (items.length === 0 && !data.erros?.length) toast.info("Nenhum resultado pra esses endereços.");
      else if (items.length > 0) toast.success(`${items.length} resultado${items.length !== 1 ? "s" : ""} encontrado${items.length !== 1 ? "s" : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao buscar. Tente novamente.");
    } finally { setLoading(false); }
  }, [enderecosList, segmento, segCustom]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/prospect/search/usage");
        setSearchQuota(data);
      } catch { /* silencioso */ }
    })();
  }, []);

  useEffect(() => {
    clearTimeout(cidadeTimer.current);
    if (cidade.length < 2) { setCidadeSugs([]); return; }
    cidadeTimer.current = setTimeout(async () => {
      setCidadeLoading(true);
      try {
        const r = await fetch("https://servicodados.ibge.gov.br/api/v1/localidades/municipios?orderBy=nome");
        const data = await r.json();
        const q = cidade.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        const matches = data
          .filter(m => m.nome.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").startsWith(q))
          .slice(0, 8)
          .map(m => `${m.nome} — ${m.microrregiao?.mesorregiao?.UF?.sigla || ""}`);
        setCidadeSugs(matches);
      } catch { setCidadeSugs([]); }
      finally { setCidadeLoading(false); }
    }, 300);
  }, [cidade]);

  useEffect(() => {
    clearTimeout(bairroTimer.current);
    setBairroSugs([]);
    if (!cidade || bairro.length < 2) return;
    bairroTimer.current = setTimeout(async () => {
      setBairroLoading(true);
      try {
        const cidadeNome = cidade.split(" — ")[0].trim();
        const r = await fetch("https://servicodados.ibge.gov.br/api/v1/localidades/municipios?orderBy=nome");
        const municipios = await r.json();
        const mun = municipios.find(m => m.nome.toLowerCase() === cidadeNome.toLowerCase());
        if (!mun) return;
        const r2 = await fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/municipios/${mun.id}/distritos`);
        const distritos = await r2.json();
        const q = bairro.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        setBairroSugs(distritos.filter(d => d.nome.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").includes(q)).slice(0, 8).map(d => d.nome));
      } catch { setBairroSugs([]); }
      finally { setBairroLoading(false); }
    }, 300);
  }, [bairro, cidade]);

  const buscar = useCallback(async () => {
    const term = segCustom.trim() || segmento;
    const cidadeNome = cidade.split(" — ")[0].trim();
    if (!term || !cidadeNome) { toast.error("Preencha a cidade e o segmento"); return; }
    setLoading(true); setResults([]); setSearched(true);
    try {
      const params = { query: term, cidade: cidadeNome };
      if (bairro.trim()) params.bairro = bairro.trim();
      const { data } = await api.get("/prospect/search", { params });
      const items = data.results || [];
      setResults(items);
      setSearchQuota({ used_today: data.used_today, remaining_today: data.remaining_today, daily_limit: data.daily_limit });
      if (items.length === 0) toast.info("Nenhum resultado. Tente outro segmento ou cidade.");
      else if (data.truncado) toast.info(`${items.length} resultado${items.length !== 1 ? "s" : ""} — cota diária de ${data.daily_limit} leads esgotada nessa busca.`);
      else toast.success(`${items.length} resultado${items.length !== 1 ? "s" : ""} encontrado${items.length !== 1 ? "s" : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao buscar. Tente novamente.");
    } finally { setLoading(false); }
  }, [cidade, bairro, segmento, segCustom]);

  const filtered = whatsappOnly ? results.filter(p => (p.phone || "").replace(/\D/g, "").length >= 10) : results;
  const totalWA = results.filter(p => (p.phone || "").replace(/\D/g, "").length >= 10).length;

  return (
    <div>
      {searchQuota && (
        <div style={{
          display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
          background: searchQuota.remaining_today <= 0 ? "rgba(239,68,68,0.08)" : "rgba(59,130,246,0.06)",
          border: `1px solid ${searchQuota.remaining_today <= 0 ? "rgba(239,68,68,0.3)" : "rgba(59,130,246,0.2)"}`,
          borderRadius: 10, padding: "10px 14px", marginBottom: 14,
        }}>
          <span style={{ fontSize: 13 }}>{searchQuota.remaining_today <= 0 ? "🚫" : "🎯"}</span>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: searchQuota.remaining_today <= 0 ? "#ef4444" : "#3b82f6" }}>
            {searchQuota.daily_limit} leads de limite por dia
          </span>
          <span style={{ fontSize: 12, color: "#a1a1aa" }}>
            {searchQuota.remaining_today <= 0
              ? `Você já puxou os ${searchQuota.daily_limit} leads de hoje. Volta amanhã ou usa o Agente.`
              : `Buscar em qualquer bairro ou cidade consome dessa mesma cota — ${searchQuota.used_today}/${searchQuota.daily_limit} já usados hoje.`}
          </span>
          <div style={{ flex: 1, minWidth: 100, height: 5, background: "#27272a", borderRadius: 20, overflow: "hidden", marginLeft: "auto" }}>
            <div style={{
              width: `${Math.min(100, (searchQuota.used_today / searchQuota.daily_limit) * 100)}%`, height: "100%",
              background: searchQuota.remaining_today <= 0 ? "#ef4444" : "#3b82f6", borderRadius: 20, transition: "width 0.3s",
            }} />
          </div>
        </div>
      )}

      <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={() => setModoBusca("segmento")}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${modoBusca === "segmento" ? "bg-blue-600 text-white" : "bg-zinc-800 text-zinc-400 hover:text-zinc-200"}`}
          >
            📍 Por bairro / segmento
          </button>
          <button
            onClick={() => setModoBusca("endereco")}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${modoBusca === "endereco" ? "bg-blue-600 text-white" : "bg-zinc-800 text-zinc-400 hover:text-zinc-200"}`}
          >
            🏢 Por endereço (CEP)
          </button>
        </div>

        {modoBusca === "segmento" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 items-end">
          <div>
            <label className="text-xs font-medium text-zinc-400 mb-1.5 block">Cidade</label>
            <AutocompleteInput value={cidade} onChange={(v) => { setCidade(v); setBairro(""); }} onSelect={(v) => { setCidade(v); setBairro(""); }}
              suggestions={cidadeSugs} loading={cidadeLoading} placeholder="Ex: São Paulo" icon={MapPin}
              onClear={() => { setCidade(""); setBairro(""); setCidadeSugs([]); setBairroSugs([]); }} />
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-400 mb-1.5 block">Bairro <span className="text-zinc-600">(opcional)</span></label>
            <AutocompleteInput value={bairro} onChange={setBairro} onSelect={setBairro}
              suggestions={bairroSugs} loading={bairroLoading} placeholder="Ex: Vila Mariana" icon={MapPin}
              onClear={() => { setBairro(""); setBairroSugs([]); }} />
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-400 mb-1.5 block">Segmento</label>
            <Select value={segmento} onValueChange={v => { setSegmento(v); setSegCustom(""); }}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-100"><SelectValue placeholder="Selecione..." /></SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                {SEGMENTOS.map(s => <SelectItem key={s.value} value={s.value} className="text-zinc-100">{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-400 mb-1.5 block">Ou busca livre</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
              <Input placeholder="clínica odontológica..." value={segCustom} onChange={e => { setSegCustom(e.target.value); setSegmento(""); }}
                className="pl-9 bg-zinc-800 border-zinc-700 text-zinc-100" onKeyDown={e => e.key === "Enter" && buscar()} />
            </div>
          </div>
        </div>
        ) : (
        <div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end mb-1">
            <div className="relative">
              <label className="text-xs font-medium text-zinc-400 mb-1.5 block">CEP</label>
              <Input placeholder="Ex: 01310-100" value={cepInput}
                onChange={e => setCepInput(e.target.value.replace(/[^\d-]/g, ""))}
                onKeyDown={e => e.key === "Enter" && adicionarEndereco()}
                className="bg-zinc-800 border-zinc-700 text-zinc-100" />
              {(cepPreviewLoading || cepPreview) && (
                <div style={{
                  fontSize: 11.5, marginTop: 5, transition: "opacity 0.3s",
                  color: cepPreview?.erro ? "#ef4444" : "#4ade80",
                }}>
                  {cepPreviewLoading ? "Consultando CEP..." : cepPreview.erro ? "CEP não encontrado." : `📍 ${cepPreview.logradouro} — ${cepPreview.bairro}, ${cepPreview.cidade}/${cepPreview.uf}`}
                </div>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-400 mb-1.5 block">Número <span className="text-zinc-600">(opcional, ajuda a mirar o prédio certo)</span></label>
              <Input placeholder="Ex: 1000" value={numeroInput} onChange={e => setNumeroInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && adicionarEndereco()}
                className="bg-zinc-800 border-zinc-700 text-zinc-100" />
            </div>
            <Button onClick={adicionarEndereco} variant="outline" className="border-zinc-700 text-zinc-200 hover:bg-zinc-800">
              <Plus className="h-4 w-4 mr-1.5" /> Adicionar CEP
            </Button>
          </div>

          {enderecosList.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3 mb-1">
              {enderecosList.map(e => (
                <span key={e.cep} className="inline-flex items-center gap-1.5 bg-zinc-800 border border-zinc-700 rounded-full px-3 py-1 text-xs text-zinc-200">
                  🏢 {e.preview}
                  <button onClick={() => removerEndereco(e.cep)} className="text-zinc-500 hover:text-red-400">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className="mt-3">
            <label className="text-xs font-medium text-zinc-400 mb-1.5 block">Segmento <span className="text-zinc-600">(opcional — deixe vazio pra pegar qualquer tipo de negócio nesses endereços)</span></label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Select value={segmento || "__todos__"} onValueChange={v => { setSegmento(v === "__todos__" ? "" : v); setSegCustom(""); }}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-100"><SelectValue placeholder="Qualquer tipo de negócio..." /></SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="__todos__" className="text-zinc-100">🌐 Todos os segmentos</SelectItem>
                  {SEGMENTOS.map(s => <SelectItem key={s.value} value={s.value} className="text-zinc-100">{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                <Input placeholder="Ou busca livre, ex: clínica odontológica..." value={segCustom} onChange={e => { setSegCustom(e.target.value); setSegmento(""); }}
                  className="pl-9 bg-zinc-800 border-zinc-700 text-zinc-100" />
              </div>
            </div>
          </div>
          <p className="text-xs text-zinc-500 mt-2">
            💡 O Google Maps só mostra empresas que já têm perfil cadastrado nele — prédios com loja/clínica costumam aparecer bem, mas escritório "de fundo" (contabilidade, advocacia etc.) às vezes não tá lá.
          </p>
        </div>
        )}

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mt-3 pt-3 border-t border-zinc-800">
          <Button onClick={modoBusca === "endereco" ? buscarPorEndereco : buscar} disabled={loading || searchQuota?.remaining_today === 0} className="bg-blue-600 hover:bg-blue-500 px-8">
            {loading ? <><Loader className="h-4 w-4 animate-spin mr-2" />Buscando...</> : <><Search className="h-4 w-4 mr-2" />Buscar</>}
          </Button>
          <label className="flex items-center gap-2 cursor-pointer select-none ml-auto">
            <Filter className="h-4 w-4 text-zinc-500" />
            <div onClick={() => setWhatsappOnly(v => !v)} className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer ${whatsappOnly ? "bg-green-500" : "bg-zinc-700"}`}>
              <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${whatsappOnly ? "translate-x-4" : "translate-x-0"}`} />
            </div>
            <span className="text-sm text-zinc-300">Somente com WhatsApp</span>
          </label>
          {searched && <span className="text-xs text-zinc-500 sm:ml-4">{filtered.length} resultado{filtered.length !== 1 ? "s" : ""} • {totalWA} com WhatsApp</span>}
        </div>
      </div>

      {loading && (
        <div className="text-center py-16">
          <div className="h-8 w-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-zinc-400 text-sm">Buscando no Google Maps… ⚡</p>
        </div>
      )}

      {!loading && searched && (
        filtered.length === 0 ? (
          <div className="text-center py-16 text-zinc-500">
            <Building2 className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="font-medium text-zinc-400">Nenhum resultado encontrado</p>
            <p className="text-sm mt-1">{whatsappOnly ? "Desative o filtro para ver todos." : "Tente mudar o segmento, bairro ou cidade"}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {filtered.map((p, i) => (
              <ResultCard key={p.place_id || i} place={p} onAddToKanban={onAddToKanban} onOpenChat={onOpenChat} />
            ))}
          </div>
        )
      )}

      {!loading && !searched && (
        <div className="text-center py-20 text-zinc-500">
          <Building2 className="h-14 w-14 mx-auto mb-4 opacity-20" />
          <p className="font-semibold text-zinc-400 text-lg">Encontre seus próximos clientes</p>
          <p className="text-sm mt-2">Busque comércios no Google Maps por cidade e segmento</p>
        </div>
      )}
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────


function LeadMarketplaceTab() {
  // Mesmo padrão do resto do arquivo: lê a classe "dark" do <html> pra
  // evitar texto claro-sobre-claro no modo Claro.
  const [isDark, setIsDark] = useState(
    () => document.documentElement.classList.contains("dark")
  );
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];
  const [uf, setUf] = useState("PE");

  const [cnaeInput, setCnaeInput] = useState("");
  const [cnaesList, setCnaesList] = useState([]);
  const [cnaeSugestoes, setCnaeSugestoes] = useState([]);
  const [cnaeSugestoesAbertas, setCnaeSugestoesAbertas] = useState(false);
  const cnaeSearchTimer = useRef(null);

  const [cidadeFiltro, setCidadeFiltro] = useState("");
  const [bairroFiltro, setBairroFiltro] = useState("");
  const [somenteMei, setSomenteMei] = useState(false);
  const [quantidade, setQuantidade] = useState(500);
  const [email, setEmail] = useState("");

  const [orcamento, setOrcamento] = useState(null);
  const [orcando, setOrcando] = useState(false);
  const orcamentoTimer = useRef(null);
  const orcamentoReqId = useRef(0);

  const [pedido, setPedido] = useState(null);
  const [gerandoPix, setGerandoPix] = useState(false);
  const pollRef = useRef(null);

  // Autocomplete de segmento — mesmo endpoint já usado na Base RFB
  useEffect(() => {
    clearTimeout(cnaeSearchTimer.current);
    const termo = cnaeInput.trim();
    if (termo.length < 2) { setCnaeSugestoes([]); return; }
    cnaeSearchTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/prospect/rfb/cnae-search", { params: { q: termo } });
        setCnaeSugestoes(data.resultados || []);
        setCnaeSugestoesAbertas(true);
      } catch { /* silencioso */ }
    }, 300);
    return () => clearTimeout(cnaeSearchTimer.current);
  }, [cnaeInput]);

  const adicionarCnae = (sugestao) => {
    let item = sugestao;
    if (!item) {
      const cnae = cnaeInput.replace(/\D/g, "");
      if (cnae.length !== 7) { toast.error("Escolha um segmento da lista, ou digite um código CNAE de 7 dígitos."); return; }
      item = { codigo: cnae, descricao: cnae };
    }
    if (cnaesList.some(c => c.codigo === item.codigo)) { toast.error("Esse CNAE já foi adicionado."); return; }
    setCnaesList([...cnaesList, item]);
    setCnaeInput("");
    setCnaeSugestoes([]);
    setCnaeSugestoesAbertas(false);
  };

  const removerCnae = (codigo) => setCnaesList(cnaesList.filter(c => c.codigo !== codigo));

  // Orçamento em tempo real (debounce) sempre que os filtros mudam
  useEffect(() => {
    clearTimeout(orcamentoTimer.current);
    setOrcamento(null);
    if (cnaesList.length === 0 || !quantidade || quantidade <= 0) return;
    orcamentoTimer.current = setTimeout(async () => {
      const meuReqId = ++orcamentoReqId.current;
      setOrcando(true);
      try {
        const { data } = await api.post("/leads/orcamento", {
          uf,
          cnaes: cnaesList.map(c => c.codigo),
          cidade: cidadeFiltro.trim(),
          bairro: bairroFiltro.trim(),
          somente_mei: somenteMei,
          quantidade,
        });
        // Guarda contra corrida: se o usuário já disparou uma consulta mais
        // nova enquanto esta ainda estava no ar, ignora essa resposta velha
        // (senão ela podia chegar depois e sobrescrever o resultado certo).
        if (meuReqId !== orcamentoReqId.current) return;
        setOrcamento(data);
      } catch (e) {
        if (meuReqId !== orcamentoReqId.current) return;
        toast.error(e?.response?.data?.detail || "Erro ao calcular orçamento.");
      } finally {
        if (meuReqId === orcamentoReqId.current) setOrcando(false);
      }
    }, 500);
    return () => clearTimeout(orcamentoTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uf, cnaesList, cidadeFiltro, bairroFiltro, somenteMei, quantidade]);

  // Polling do status do pedido até entregar (ou falhar)
  useEffect(() => {
    if (!pedido || pedido.status === "entregue" || pedido.status === "falhou") return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/leads/pedidos/${pedido.order_id}`);
        setPedido(p => ({ ...p, ...data }));
      } catch { /* silencioso */ }
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [pedido]);

  const gerarPix = async () => {
    if (cnaesList.length === 0) { toast.error("Adicione pelo menos um segmento (CNAE)."); return; }
    if (!email.trim() || !email.includes("@")) { toast.error("Informe um e-mail válido para receber o mailing."); return; }
    setGerandoPix(true);
    try {
      const { data } = await api.post("/leads/pedidos", {
        uf,
        cnaes: cnaesList.map(c => c.codigo),
        cidade: cidadeFiltro.trim(),
        bairro: bairroFiltro.trim(),
        somente_mei: somenteMei,
        quantidade,
        email_entrega: email.trim(),
      });
      setPedido({ ...data, status: "aguardando_pagamento" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao gerar o Pix. Tente novamente.");
    } finally {
      setGerandoPix(false);
    }
  };

  const copiarPix = () => {
    navigator.clipboard.writeText(pedido.qr_code);
    toast.success("Código Pix copiado!");
  };

  const novoPedido = () => {
    clearInterval(pollRef.current);
    setPedido(null);
    setOrcamento(null);
  };

  const formatBRL = (n) => `R$ ${Number(n).toFixed(2).replace(".", ",")}`;

  return (
    <div>
      <div style={{ background: "linear-gradient(135deg, rgba(34,197,94,0.08), rgba(96,165,250,0.04))", border: "1px solid rgba(34,197,94,0.25)", borderRadius: 14, padding: 16, marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 18 }}>🛒</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: (isDark ? "#f4f4f5" : "#18181b") }}>Comprar Leads</span>
        </div>
        <p style={{ fontSize: 12, color: (isDark ? "#a1a1aa" : "#52525b"), margin: 0 }}>
          Monte seu mailing sob medida direto da nossa própria base da Receita Federal — escolha segmento, região e quantidade, pague via Pix e receba o arquivo pronto no seu e-mail.
        </p>
      </div>

      {pedido ? (
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
          {pedido.status === "entregue" ? (
            <div className="text-center py-6">
              <CheckCircle2 className="h-10 w-10 text-green-500 mx-auto mb-2" />
              <p className="font-semibold text-green-400">Mailing entregue! 🎉</p>
              <p className="text-sm text-zinc-400 mt-1">Enviamos {pedido.quantidade_final} leads para {pedido.email_entrega || email}.</p>
              <Button onClick={novoPedido} variant="outline" className="mt-4 border-zinc-700 text-zinc-200 hover:bg-zinc-800">Fazer novo pedido</Button>
            </div>
          ) : pedido.status === "falhou" ? (
            <div className="text-center py-6">
              <XCircle className="h-10 w-10 text-red-500 mx-auto mb-2" />
              <p className="font-semibold text-red-400">Algo deu errado ao gerar seu mailing.</p>
              <p className="text-sm text-zinc-400 mt-1">Fale com o suporte informando o pedido {pedido.order_id}.</p>
              <Button onClick={novoPedido} variant="outline" className="mt-4 border-zinc-700 text-zinc-200 hover:bg-zinc-800">Tentar de novo</Button>
            </div>
          ) : pedido.status === "processando" || pedido.status === "pago" ? (
            <div className="text-center py-6">
              <Loader className="h-8 w-8 animate-spin text-blue-400 mx-auto mb-2" />
              <p className="font-semibold text-zinc-100">Pagamento confirmado — preparando seu mailing...</p>
              <p className="text-sm text-zinc-400 mt-1">Isso leva só alguns instantes. Não feche esta página.</p>
            </div>
          ) : (
            <div>
              <div className="text-center mb-3">
                <div className="text-2xl font-bold text-zinc-100">{formatBRL(pedido.amount)}</div>
                <div className="text-xs text-zinc-500">{pedido.quantidade_final} leads</div>
              </div>
              {pedido.qr_code_base64 && (
                <div className="flex justify-center mb-4">
                  <img
                    src={`data:image/png;base64,${pedido.qr_code_base64}`}
                    alt="QR Code Pix"
                    className="w-48 h-48 border border-zinc-700 rounded-lg"
                  />
                </div>
              )}
              <div className="flex gap-2 mb-3">
                <div className="flex-1 bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-xs font-mono text-zinc-300 truncate">
                  {pedido.qr_code}
                </div>
                <button onClick={copiarPix} className="shrink-0 px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-md">
                  <Copy className="h-4 w-4" />
                </button>
              </div>
              <p className="text-xs text-zinc-500 text-center">
                Assim que o pagamento cair, o mailing é gerado e enviado automaticamente pra {email}.
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end mb-1">
            <div style={{ position: "relative", gridColumn: "span 2" }}>
              <label className="text-xs font-medium text-zinc-400 mb-1.5 block">Segmento (ex: farmácia, restaurante...)</label>
              <Input placeholder="Digite o segmento do negócio..." value={cnaeInput}
                onChange={e => setCnaeInput(e.target.value)}
                onFocus={() => cnaeSugestoes.length > 0 && setCnaeSugestoesAbertas(true)}
                onBlur={() => setTimeout(() => setCnaeSugestoesAbertas(false), 150)}
                onKeyDown={e => e.key === "Enter" && adicionarCnae()}
                className="bg-zinc-800 border-zinc-700 text-zinc-100" />
              {cnaeSugestoesAbertas && cnaeSugestoes.length > 0 && (
                <div style={{
                  position: "absolute", top: "100%", left: 0, right: 0, marginTop: 4, zIndex: 20,
                  background: "#18181b", border: "1px solid #3f3f46", borderRadius: 8,
                  maxHeight: 260, overflowY: "auto", boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
                }}>
                  {cnaeSugestoes.map(s => (
                    <button
                      key={s.codigo}
                      onClick={() => adicionarCnae(s)}
                      style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 12px", background: "none", border: "none", borderBottom: "1px solid #27272a", cursor: "pointer", fontSize: 12.5, color: "#e4e4e7" }}
                      onMouseDown={e => e.preventDefault()}
                    >
                      {s.descricao} <span style={{ color: "#71717a", fontSize: 11 }}>({s.codigo})</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-400 mb-1.5 block">Estado</label>
              <select value={uf} onChange={e => setUf(e.target.value)} className="w-full bg-zinc-800 border border-zinc-700 text-zinc-100 text-sm rounded-md p-2.5">
                {UFS.map(u => <option key={u} value={u} style={{ color: "#18181b" }}>{u}</option>)}
              </select>
            </div>
          </div>
          <Button onClick={() => adicionarCnae()} variant="outline" size="sm" className="border-zinc-700 text-zinc-200 hover:bg-zinc-800 mb-1">
            <Plus className="h-4 w-4 mr-1.5" /> Adicionar segmento
          </Button>
          <p className="text-xs text-zinc-500 mt-1">💡 Base própria com todos os estados do Brasil, atualizada direto da Receita Federal.</p>

          {cnaesList.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3 mb-1">
              {cnaesList.map(c => (
                <span key={c.codigo} className="inline-flex items-center gap-1.5 bg-zinc-800 border border-zinc-700 rounded-full px-3 py-1 text-xs text-zinc-200">
                  📋 {c.descricao}
                  <button onClick={() => removerCnae(c.codigo)} className="text-zinc-500 hover:text-red-400">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            <div>
              <label className="text-xs font-medium text-zinc-400 mb-1.5 block">Cidade <span className="text-zinc-600">(opcional)</span></label>
              <Input placeholder="Ex: Recife" value={cidadeFiltro} onChange={e => setCidadeFiltro(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-zinc-100" />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-400 mb-1.5 block">Bairro <span className="text-zinc-600">(opcional)</span></label>
              <Input placeholder="Ex: Boa Viagem" value={bairroFiltro} onChange={e => setBairroFiltro(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-zinc-100" />
            </div>
          </div>

          <label className="flex items-center gap-2 mt-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={somenteMei}
              onChange={e => setSomenteMei(e.target.checked)}
              className="h-4 w-4 rounded border-zinc-600"
            />
            <span className="text-sm text-zinc-200">🏷️ Somente MEI</span>
            <span className="text-xs text-zinc-500">— filtra só empresas classificadas como Microempreendedor Individual</span>
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            <div>
              <label className="text-xs font-medium text-zinc-400 mb-1.5 block">Quantidade de leads desejada</label>
              <Input type="number" min={1} max={50000} value={quantidade}
                onChange={e => setQuantidade(Math.max(1, Math.min(50000, parseInt(e.target.value) || 1)))}
                className="bg-zinc-800 border-zinc-700 text-zinc-100" />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-400 mb-1.5 block">E-mail para receber o mailing</label>
              <Input type="email" placeholder="seuemail@empresa.com" value={email} onChange={e => setEmail(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-zinc-100" />
            </div>
          </div>

          {orcando && <div className="text-xs text-zinc-500 mt-3">Calculando orçamento...</div>}
          {orcamento && !orcando && (
            <div className="mt-4 rounded-lg p-3" style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.25)" }}>
              <div className="flex items-center justify-between text-sm">
                <span className="text-zinc-300">{orcamento.disponivel} leads disponíveis com esse filtro</span>
                <span className="font-bold text-green-400">{formatBRL(orcamento.preco_total)}</span>
              </div>
              {orcamento.quantidade_final < quantidade && (
                <div className="text-xs text-amber-400 mt-1">
                  Você vai receber {orcamento.quantidade_final} leads (é o máximo disponível pra esse filtro, menos que os {quantidade} pedidos)
                </div>
              )}
            </div>
          )}

          <div className="mt-4 pt-3 border-t border-zinc-800">
            <Button onClick={gerarPix} disabled={gerandoPix || !orcamento} className="bg-green-600 hover:bg-green-500 px-8">
              {gerandoPix ? <><Loader className="h-4 w-4 animate-spin mr-2" />Gerando Pix...</> : <><ShoppingBag className="h-4 w-4 mr-2" />Gerar Pix e Comprar</>}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


export default function ProspectPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isFree = !user?.is_admin && (!user?.plan_key || user?.plan_key === "free");

  // Todos os hooks antes de qualquer return condicional (regra do React)
  const [activeTab, setActiveTab] = useState("busca"); // "busca" | "pipeline" | "agente"
  const [chatPlace, setChatPlace] = useState(null);
  const [pipelineLeads, setPipelineLeads] = useState([]);
  const [pipelineLoading, setPipelineLoading] = useState(true);
  const [isDark, setIsDark] = useState(
    () => document.documentElement.classList.contains("dark")
  );
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  const fetchPipeline = useCallback(() => {
    api.get("/prospect/pipeline")
      .then(({ data }) => setPipelineLeads(data.leads || []))
      .catch(() => {})
      .finally(() => setPipelineLoading(false));
  }, []);

  // Migração única dos leads que estavam só no localStorage (versão antiga,
  // antes do Kanban virar backend) — sem isso, esses leads se perderiam.
  useEffect(() => {
    let old = [];
    try { old = JSON.parse(localStorage.getItem(PIPELINE_KEY) || "[]"); } catch { old = []; }
    if (!old.length) return;

    (async () => {
      try {
        const { data } = await api.get("/prospect/pipeline");
        const existingPhones = new Set((data.leads || []).map(l => (l.phone || "").replace(/\D/g, "")));
        const toMigrate = old.filter(l => !existingPhones.has((l.phone || "").replace(/\D/g, "")));
        for (const lead of toMigrate) {
          try {
            await api.post("/prospect/pipeline/add", {
              name: lead.name, phone: lead.phone || "", segment: lead.segment || "",
              address: lead.address || "", col: lead.col || "lead",
            });
          } catch { /* segue migrando os outros mesmo se um falhar */ }
        }
        localStorage.removeItem(PIPELINE_KEY);
        if (toMigrate.length) fetchPipeline();
      } catch { /* sem rede agora — tenta de novo na próxima visita */ }
    })();
  }, [fetchPipeline]);

  // Carrega o pipeline ao montar e fica de olho a cada 20s — assim, se a IA
  // mover um card sozinha (lead respondeu no WhatsApp), o Kanban atualiza
  // sem precisar recarregar a página.
  useEffect(() => {
    fetchPipeline();
    const interval = setInterval(fetchPipeline, 20000);
    return () => clearInterval(interval);
  }, [fetchPipeline]);

  const handleAddToKanban = async (place) => {
    const exists = pipelineLeads.find(l => l.phone === (place.phone || "").replace(/\D/g, ""));
    if (exists) { toast.info("Este lead já está no pipeline!"); return; }
    try {
      const { data } = await api.post("/prospect/pipeline/add", {
        name: place.name,
        phone: place.phone || "",
        segment: place.category || "",
        address: place.address || "",
      });
      setPipelineLeads(prev => [data.lead, ...prev]);
      toast.success(`${place.name} adicionado ao pipeline! 🎯`);
      setActiveTab("pipeline");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível adicionar ao pipeline.");
    }
  };

  const handleManualAddLead = async ({ name, phone, cnpj, plano_negociado, observacoes, value }) => {
    const phoneDigits = (phone || "").replace(/\D/g, "");
    const exists = pipelineLeads.find(l => l.phone === phoneDigits);
    if (exists) { toast.info("Este lead já está no pipeline!"); return false; }
    try {
      const { data } = await api.post("/prospect/pipeline/add", {
        name: name || phone,
        phone,
        cnpj,
        plano_negociado,
        observacoes,
        value,
      });
      setPipelineLeads(prev => [data.lead, ...prev]);
      toast.success(`${name || phone} adicionado à coluna Leads! 🎯`);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível adicionar ao pipeline.");
      return false;
    }
  };

  const handleEditLeadDetails = async (leadId, { cnpj, plano_negociado, observacoes, value }) => {
    try {
      const { data } = await api.post(`/prospect/pipeline/${leadId}/details`, {
        cnpj, plano_negociado, observacoes, value,
      });
      setPipelineLeads(prev => prev.map(l => l.id === leadId ? data.lead : l));
      toast.success("Detalhes do lead atualizados!");
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível salvar os detalhes.");
      return false;
    }
  };

  const handleMoveLead = async (id, col) => {
    setPipelineLeads(prev => prev.map(l => l.id === id ? { ...l, col } : l));
    try {
      await api.post(`/prospect/pipeline/${id}/move`, { col });
    } catch (e) {
      toast.error("Não foi possível mover o lead. Tente de novo.");
      fetchPipeline();
    }
  };

  const handleDeleteLead = async (id) => {
    setPipelineLeads(prev => prev.filter(l => l.id !== id));
    try {
      await api.delete(`/prospect/pipeline/${id}`);
    } catch (e) {
      toast.error("Não foi possível remover o lead. Tente de novo.");
      fetchPipeline();
    }
  };

  const handleBulkMoveLeadsToPerdido = async () => {
    const qtd = pipelineLeads.filter(l => l.col === "lead").length;
    if (qtd === 0) { toast.info("Não tem nenhum card na coluna Lead."); return; }
    if (!window.confirm(`Isso vai mover TODOS os ${qtd} cards que estão em "Lead" pra "Perdido". Confirma?`)) return;
    try {
      const { data } = await api.post("/prospect/pipeline/bulk-move-to-perdido");
      toast.success(`${data.movidos} card(s) movido(s) pra Perdido.`);
      fetchPipeline();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao mover os leads.");
    }
  };

  const tabs = [
    { id: "busca",    label: "Busca",    icon: Search,   count: null },
    { id: "agente",   label: "Agente",   icon: Bot,      count: null },
    { id: "mailing",  label: "Mailing",  icon: Send,     count: null },
    { id: "marketplace", label: "Comprar Leads", icon: ShoppingBag, count: null },
    { id: "email",    label: "E-mail",   icon: Mail,     count: null },
    { id: "pipeline", label: "Pipeline", icon: Target,   count: pipelineLeads.length || null },
  ];

  // Bloqueio para plano Free — renderizado após todos os hooks
  if (isFree) {
    return (
      <div className="p-6 md:p-8 max-w-2xl mx-auto">
        <div style={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 16, padding: "48px 40px", textAlign: "center" }}>
          <div style={{ width: 64, height: 64, borderRadius: 16, background: "rgba(167,139,250,0.12)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
            <Target size={30} style={{ color: "#a78bfa" }} />
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "#f4f4f5", marginBottom: 8 }}>
            Prospecção Automática de Leads
          </h1>
          <p style={{ fontSize: 14, color: "#71717a", maxWidth: 380, margin: "0 auto 28px", lineHeight: 1.6 }}>
            Recurso exclusivo para planos a partir do <strong style={{ color: "#f4f4f5" }}>Pro</strong>.
            Faça upgrade e deixe o agente encontrar e abordar clientes no WhatsApp por você, automaticamente.
          </p>
          <div style={{ background: "#1c1c1f", border: "1px solid #2d2d30", borderRadius: 12, padding: "16px 20px", marginBottom: 24, textAlign: "left" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#71717a", letterSpacing: "0.06em", marginBottom: 10 }}>O QUE VOCÊ GANHA AO FAZER UPGRADE</div>
            {[
              { icon: "🔍", text: "Busca automática de empresas no Google Maps por cidade e bairro" },
              { icon: "🤖", text: "Agente envia a 1ª mensagem de prospecção no WhatsApp sem você tocar" },
              { icon: "💬", text: "IA responde automaticamente e classifica os leads no funil de vendas" },
              { icon: "🎯", text: "Pipeline Kanban: Lead → Contato → Proposta → Fechado" },
              { icon: "🔄", text: "Rotação de bairros para atingir até 50 novos leads por dia" },
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: i < 4 ? 10 : 0 }}>
                <span style={{ fontSize: 15, flexShrink: 0 }}>{item.icon}</span>
                <span style={{ fontSize: 13, color: "#a1a1aa", lineHeight: 1.5 }}>{item.text}</span>
              </div>
            ))}
          </div>
          <button
            onClick={() => navigate("/planos")}
            style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "linear-gradient(135deg, #7c3aed, #6d28d9)", border: "none", borderRadius: 10, padding: "12px 28px", fontSize: 14, fontWeight: 700, color: "#fff", cursor: "pointer", boxShadow: "0 4px 20px rgba(124,58,237,0.35)" }}>
            <Sparkles size={16} /> Ver planos e fazer upgrade
          </button>
          <p style={{ fontSize: 11.5, color: "#52525b", marginTop: 12 }}>
            Planos a partir de R$ 49/mês
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100 flex items-center gap-2">
          <Building2 className="h-6 w-6 text-blue-400" />
          Minhas Estratégias de Vendas
        </h1>
        <p className="text-zinc-600 dark:text-zinc-400 text-sm mt-1">Encontre comércios, prospecte via WhatsApp e gerencie seu pipeline de vendas</p>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20, borderBottom: "1px solid hsl(var(--border))" }}>
        {tabs.map(t => {
          const active = activeTab === t.id;
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
              padding: "11px 20px",
              border: active ? "1px solid hsl(var(--border))" : "1px solid transparent",
              borderBottom: active ? "1px solid var(--bg2)" : "1px solid transparent",
              borderRadius: "12px 12px 0 0",
              background: active ? "var(--bg2)" : "hsl(var(--muted))",
              color: active ? "#3b82f6" : "var(--text1)",
              fontSize: 13.5, fontWeight: active ? 700 : 600, cursor: "pointer",
              opacity: active ? 1 : 0.7,
              marginBottom: -1, transition: "all 0.15s",
            }}>
              <Icon size={15} />
              {t.label}
              {t.count && (
                <span style={{ background: "#3b82f620", color: "#3b82f6", border: "1px solid #3b82f640", borderRadius: 20, padding: "1px 7px", fontSize: 11, fontWeight: 700 }}>
                  {t.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {activeTab === "busca"    && <BuscaTab onAddToKanban={handleAddToKanban} onOpenChat={setChatPlace} />}
      {activeTab === "agente"   && <AgentTab onDone={fetchPipeline} />}
      {activeTab === "mailing"  && <MailingTab />}
      {activeTab === "marketplace" && <LeadMarketplaceTab />}
      {activeTab === "email"    && <EmailMassaTab />}
      {activeTab === "pipeline" && (
        <PipelineTab leads={pipelineLeads} loading={pipelineLoading}
          onMove={handleMoveLead} onDelete={handleDeleteLead} onOpenChat={setChatPlace}
          onAddManual={handleManualAddLead} onEditDetails={handleEditLeadDetails}
          onBulkMoveLeadsToPerdido={handleBulkMoveLeadsToPerdido} />
      )}

      {chatPlace && <WhatsAppChat place={chatPlace} onClose={() => setChatPlace(null)} />}
    </div>
  );
}
